import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { connect } from 'react-redux';
import { getBackend } from '../backend';
import { detectFamily, classifyDevice, readDevice, writeDevice, reconcileTypes, deviceFields, otherDeviceKeys, cameraCounts, isModernFormat, readConnector, connectorFields, otherConnectorKeys, listConnectorPos, posSection, listConfigCameras, cameraSection, boolFromCfg, addDiscoveredCameras, isStreamUrl, nextCameraNumber, removeCamera, replaceCameraStream, cameraType, cameraIpFromSection, setEngineKey, setConnectorKey, hasConnectorBlock, enableConnector, disableConnector } from './configParser';
import type17 from './pos-types/type17.json';
import type19 from './pos-types/type19.json';
import type20 from './pos-types/type20.json';
import type21 from './pos-types/type21.json';
import { askConfirm } from './confirmService';
import { isDeviceDirty, canonConfigEqual, configChanges, revertChange } from './configDirty';
import { saveDraft, loadDraft, clearDraft } from './configDraft';
import SheetSelect from './SheetSelect';
import Sheet from '../common/Sheet';
import AlertModal from '../common/AlertModal';
import { Row, ListRow, SetupListPanel, SetupDrill, Badge, OtherSettings, UnknownField, orgSubLabel, confirmDiscardIfDirty, useReportDirty, isMasterRole, resolveDisp, Field, timezoneSelectOptions, deviceHasAdmin, serverErrorText, SetupReadOnlyContext, SetupNavContext, useAutoSelectFirstInLandscape, useIsSetupMobileLandscape, usePersistedSelection, useSaveSelection, setupPersistedSelection, saveSetupSelection, setupPersistedSubTab, saveSetupSubTab } from './setupCommon';
import { fieldAccess } from './setupAccess';   // the single source: '<menu>.<field>' → access; unknown → master-edit/local-readonly
import { getProductConfig } from '../backend/productConfig';
import DiscoverCamerasSheet from './DiscoverCamerasSheet';
import { clearDiscoverDraft } from './discoverDraft';
import ScalewatcherRow from './ScalewatcherEnroll';

const POS_TYPES = { 17: type17, 19: type19, 20: type20, 21: type21 };
const POS_TYPE_LIST = [type17, type19, type20, type21];

// A connector value is always a string in config.files.connector; booleans serialize to '0'/'1'.
const posValStr = (v) => (v === true ? '1' : v === false ? '0' : String(v));

// Port auto-assignment + uniqueness (#86bbpgbdz). Only LPR (6511-6520) and Scale Watcher (6501-6510) listen on
// a TCP port; the range lives on the schema's tcp_port field (min/max) so it also drives the number stepper.
// A new POS grabs the LOWEST FREE port in its range — so a port freed by deleting a lane is reclaimed — and no
// two POS on a device may share one (enforced here + in validate() + server-side in cloudapi deviceConfig).
const portRangeOf = (schema) => {
  const f = schema && (schema.fields || []).find((x) => x.key === 'tcp_port');
  return f && f.min != null && f.max != null ? [Number(f.min), Number(f.max)] : null;
};
// Every tcp_port currently assigned to a posN lane in a connector config (finite numbers only).
const usedPosPorts = (config) => {
  const out = [];
  const conn = config && config.files && config.files.connector;
  if (!conn) return out;
  Object.keys(conn).forEach((k) => {
    if (!/^pos\d+$/.test(k)) return;
    const p = Number(conn[k].tcp_port);
    if (Number.isFinite(p)) out.push(p);
  });
  return out;
};
// Lowest free port in the schema's range for a given config; null when the type is port-less or the range is full.
const nextFreePort = (schema, config) => {
  const range = portRangeOf(schema);
  if (!range) return null;
  const [min, max] = range;
  const used = new Set(usedPosPorts(config));
  for (let p = min; p <= max; p += 1) { if (!used.has(p)) return p; }
  return null;   // range full — the Add option for this type is disabled upstream
};
// Inline (real-time) validation for the port field in the detail editor: out-of-range or already taken by
// another POS. Empty while the field is blank (required-ness is a push-time check, not an inline nag). (#86bbpgbdz)
const tcpPortError = (field, value, otherPorts, pristinePort) => {
  if (value == null || value === '') return '';
  // Grandfather an UNCHANGED port (matches validate() + cloudapi) — don't redden a legacy value the user hasn't
  // touched, even if it now sits outside the range. (#86bbpgbdz)
  if (String(value) === String(pristinePort == null ? '' : pristinePort)) return '';
  if (!/^\d+$/.test(String(value).trim())) return 'Enter a whole number.';
  const port = Number(value);
  const min = Number(field.min), max = Number(field.max);
  if (port < min || port > max) return `Must be between ${min} and ${max}.`;
  if (otherPorts && otherPorts.has(port)) return `Port ${port} is already used by ${otherPorts.get(port)}.`;
  return '';
};
// port number -> label of the POS lane holding it, EXCLUDING one lane (the one being edited), for the inline
// duplicate check. (#86bbpgbdz)
const portOwnersExcept = (config, exceptNum) => {
  const m = new Map();
  const conn = config && config.files && config.files.connector;
  if (!conn) return m;
  Object.keys(conn).forEach((k) => {
    const mm = /^pos(\d+)$/.exec(k);
    if (!mm || Number(mm[1]) === Number(exceptNum)) return;
    const p = Number(conn[k].tcp_port);
    if (Number.isFinite(p)) m.set(p, conn[k].name || ('POS ' + mm[1]));
  });
  return m;
};

// Which config section a POS field's value lives in: a section may declare store:"scale"
// (Scale Watcher sensing/tuning/antitheft), which routes to [scaleN]; everything else is [posN].
const posFieldStore = (schema, key) => {
  const field = schema && schema.fields.find((f) => f.key === key);
  if (!field) return 'pos';
  const section = schema.sections && schema.sections.find((s) => s.id === field.section);
  return (section && section.store) || 'pos';
};

// The default seed for a [scaleN] section (all scale-store fields) — used when the scale
// Enable toggle first creates the section. Mirrors connector.html SCALE_DEFAULTS.
const scaleSeed = (schema) => {
  const sec = {};
  (schema.fields || []).forEach((field) => {
    if (posFieldStore(schema, field.key) === 'scale') sec[field.key] = posValStr(field.default);
  });
  return sec;
};

// Per-item edit gating (access:{master,local}) + the master-bit resolver now live in setupCommon
// (isMasterRole / DEFAULT_ACCESS / resolveDisp) — THE shared mechanism across all cloud setup screens.

// conf_rev is owned server-side now — cloudapi modifyDeviceConfig does the atomic compare-and-increment
// against the live DB value (per-file: connector AND dragon). The client no longer bumps it.

// This device's ENABLED cameras, for the POS camera-overlay row editor's dropdown. Reads the same
// config the Camera screen does (config.files.dragon camN — number + name + enabled) and keeps only
// enabled=1, so a POS lane can only point at a camera that's actually live. [{ num, name }].
const enabledCameras = (config) =>
  listConfigCameras(config)
    .filter((n) => boolFromCfg(cameraSection(config, n).enabled))
    .map((n) => ({ num: n, name: cameraSection(config, n).name || '' }));

// The event vocabulary for an overlays field (SW/PSCL images / images_inbound): the options of the
// first select field in the field's `eventsFrom` section (start_transaction shares its option set with
// finish_transaction), normalized to plain event strings.
const eventOptions = (schema, sectionId) => {
  const evField = (schema.fields || []).find((f) => f.section === sectionId && f.kind === 'select' && Array.isArray(f.options));
  return evField ? evField.options.map((o) => (typeof o === 'string' ? o : String(o.value))) : [];
};

// --- camera-overlay serialization (matches connector.html / connector_config.py byte-for-byte) ---

// SW/PSCL `images`: `~`-joined `cam-EventName-overlay` triples. Parse tolerates junk (drops non-triples).
const parseOverlays = (raw) =>
  String(raw || '').split('~').map((seg) => seg.split('-'))
    .filter((p) => p.length === 3 && p[0])
    .map((p) => ({ cam: p[0], event: p[1], overlay: p[2] === '1' }));
const serializeOverlays = (rows) =>
  rows.filter((r) => r.cam).map((r) => `${r.cam}-${r.event}-${r.overlay ? '1' : '0'}`).join('~');

// LPR: the union of the `assoc` + `overlay` comma-lists is the selected-camera set; a camera's overlay
// flag = its membership in `overlay`. (David 2026-08-20: selecting a camera associates it; overlay is
// an extra per-camera switch.) De-duped, association order first.
const parseLprCameras = (posSec) => {
  const list = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
  const overlay = new Set(list(posSec.overlay));
  const rows = [];
  const seen = new Set();
  for (const cam of [...list(posSec.assoc), ...overlay]) {
    if (!seen.has(cam)) { seen.add(cam); rows.push({ cam, overlay: overlay.has(cam) }); }
  }
  return rows;
};
// Every selected camera → `assoc` (association = selection); the overlay-checked subset → `overlay`.
const serializeLprCameras = (rows) => {
  const valid = rows.filter((r) => r.cam);
  return {
    assoc: valid.map((r) => r.cam).join(','),
    overlay: valid.filter((r) => r.overlay).map((r) => r.cam).join(','),
  };
};

/**
 * CloudDeviceSetup — the edge NVR boxes ("Device" tab; unified-viewer-setup-design.md §4.2;
 * ported from isite admin/forms/device/DeviceForm.js into our Setup aesthetic).
 *
 * A device self-registers into cloudapi when it comes online, so this screen is UPDATE-only (no
 * add / no delete). Its job is ORG ASSIGNMENT: give the box a name and place it under a customer ▸
 * site. The rest are read-only facts (Device ID, IP, Backend ID, Online, Last Event, Camera Count)
 * plus the device Type — Modern (dragon) vs Legacy — which is exactly where the two config families
 * diverge (dragon [camN] + rich [global]/[server]/… vs legacy [cameraN] + [libaiengine]).
 *
 * The save MUST round-trip the device's current parsed config: modifyOrCreateDevice re-writes
 * JSDeviceConfig from body.config, so we pass it back UNCHANGED (never reshaped) — org assignment
 * never touches the config blob. Editing the config itself is the Cameras screen's job.
 *
 * LAYOUT — shared mobile drill-down (SetupDrill), the locked template.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const STALE_DAYS = 7;

/**
 * Format a device Last-Event timestamp for display. The backend stores `sLastEventTS` as a raw
 * `YYYYMMDDHHMMSS` wall-clock string (verified on web1, e.g. "20260723125815") whose TIMEZONE is
 * indeterminate from the cloud side (verified against engine/backend code 2026-07-23):
 *   • dragon devices write device-LOCAL time (all `localtime_r`, dragon/camera_thread.c:374),
 *   • legacy devices write UTC by default but device-local when `[global]localtime=1`
 *     (isite/engine/config.h:18 `LOCALTIME 0`, engine.c:225),
 *   • backend-generated online/offline events write the backend HOST's local time
 *     (isite/backend/server_thread.c:308).
 * The device transmits no zone at all — there is no timezone key in dragon/legacy config and none in
 * the register/event PMSG. So we deliberately do NOT convert or attach a specific zone: reformat the
 * 14-digit form to "Mon D, YYYY, h:mm AM/PM" by SLICING the digits (never `new Date()`, which would
 * shift by the browser zone) and show it exactly as recorded. cloudapi's `convertedTS` (a hard-coded
 * UTC→projectTZ conversion, auth.js:181) is intentionally IGNORED — it is only sound for a default
 * legacy device and is WRONG for dragon. The real (source) fix is captured in
 * docs/unified-viewer-last-event-timezone-fix.md. Non-14-digit input passes through unchanged.
 */
export const fmtLastEvent = (v) => {
  const s = v == null ? '' : String(v).trim();
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})\d{2}$/.exec(s);
  if (!m) return s;                               // empty, or already-formatted (convertedTS) → as-is
  const [, y, mo, d, hh, mi] = m;
  const mon = MONTHS[Number(mo) - 1] || mo;
  const h24 = Number(hh);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${mon} ${Number(d)}, ${y}, ${h12}:${mi} ${h24 < 12 ? 'AM' : 'PM'}`;
};

// True when the raw YYYYMMDDHHMMSS timestamp is older than STALE_DAYS. Used to detect devices
// whose fOnline flag is stuck (device went offline ungracefully, backend never cleared the flag).
// The timestamp's timezone is indeterminate (see fmtLastEvent), but a 7-day window is coarse
// enough that timezone differences don't matter.
export const eventAgeDays = (ts) => {
  const s = ts == null ? '' : String(ts).trim();
  const m = /^(\d{4})(\d{2})(\d{2})/.exec(s);
  if (!m) return Infinity;
  const eventDate = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Math.floor((Date.now() - eventDate.getTime()) / 86400000);
};

export const isEventStale = (ts) => eventAgeDays(ts) > STALE_DAYS;

const fmtAgeDays = (days) => {
  if (!isFinite(days)) return 'no events';
  if (days < 1) return 'today';
  if (days === 1) return '1 day';
  if (days < 365) return days + ' days';
  const yrs = Math.floor(days / 365);
  return yrs === 1 ? '~1 year' : '~' + yrs + ' years';
};

// The device Type shown to the admin. Honest about an un-pushed (empty) config — detectFamily
// defaults empty→dragon for the camera editor, but here we surface "Unknown" so a legacy box that
// hasn't uploaded a config isn't mislabeled "Modern".
const familyBadge = (config) => {
  if (isModernFormat(config)) return { label: 'Dragon', color: '#1976d2' };
  const keys = config && typeof config === 'object' ? Object.keys(config) : [];
  if (!keys.length) return { label: 'Unknown', color: '#6b7280' };
  return detectFamily(config).key === 'dragon'
    ? { label: 'Dragon', color: '#1976d2' }
    : { label: 'Legacy', color: '#8a6d1f' };
};


// cloudapi modifyOrCreateDevice rejects a device name longer than this (admin.js) — cap the input to match.
const DEVICE_NAME_MAX = 26;

const deviceToForm = (d) => {
  const config = d.sConfig && typeof d.sConfig === 'object' ? d.sConfig : {};
  // Device-level engine config is editable only when the family is KNOWN (a non-empty config); an
  // Unknown/empty device stays org-assignment-only (cfg=null → the blob round-trips untouched, and we
  // don't fabricate dragon sections into a box we couldn't classify).
  const hasConfig = isModernFormat(config) || Object.keys(config).length > 0;
  const cfg = hasConfig ? readDevice(config, detectFamily(config)) : null;
  const conn = hasConfig ? readConnector(config) : null;
  return {
    id: d.bID,
    deviceId: d.sDeviceID,
    name: d.sName || '',
    customerId: d.bCustomerID || '',       // backend COALESCEs unassigned → 0; treat as empty
    projectId: d.bCustomerProjectID || '',
    config,
    cfg,
    conn,
    // read-only facts
    ip: d.sCurrentIP || '',
    backendId: d.sBackendID || '',
    online: d.fOnline === 1 || d.fOnline === true,
    stale: (d.fOnline === 1 || d.fOnline === true) && isEventStale(d.sLastEventTS),
    // Raw device wall-clock, shown as-is (see fmtLastEvent). We deliberately do NOT prefer cloudapi's
    // `convertedTS` (UTC→projectTZ): that conversion assumes the stored value is UTC, which holds only
    // for a default legacy device — it double-shifts dragon devices (which store LOCAL time) and
    // backend online/offline events (backend-host local). Real fix is upstream (device reports its
    // zone / source normalizes) — docs/unified-viewer-last-event-timezone-fix.md.
    lastEvent: d.sLastEventTS || '',
    // NB: JSDevice.bCameras is deliberately NOT carried — it's an unreliable device-reported value
    // cloudapi never writes on the admin path. The camera count is derived from the config instead
    // (cameraCounts), so the Device screen and the Camera screen always agree.
  };
};

const CloudDeviceSetup = (props) => {
  const [devices, setDevices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [pristine, setPristine] = useState(null); // saved/loaded snapshot; dirty = form differs from it
  const latestConfigRef = useRef(null);    // latest form.config, tracked in render so addPos reads fresh state (not a stale callback closure)
  const latestPristineRef = useRef(null);  // latest pristine.config, so addPos never reuses a posN still held by the baseline (review #1)
  const advSnapshotRef = useRef(null);     // form.config snapshot when the Advanced overlay opened — DISCARD rolls back to it (#86bbm58f7 piece 2)
  const preDisableConnRef = useRef(null);  // files.connector captured at Disable POS, so Re-enable undoes a STAGED disable, restoring even un-pushed connector edits (86bc1nuf9 review)
  const draftTimer = useRef(null);         // debounce for persisting the working draft to sessionStorage (#86bbm58f7 refresh-persist)
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState('');
  const [activeTab, setActiveTabRaw] = useState('general');
  const setActiveTab = useCallback((id) => { setActiveTabRaw(id); saveSetupSubTab('devices', id); }, []);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showReview, setShowReview] = useState(false);   // "Push Changes to Device" review sheet (#86bbm58f7 piece 4)
  const [alert, setAlert] = useState(null);              // push result modal: { type:'info'|'error', title, message }
  const [posResetToken, setPosResetToken] = useState(0); // bumped on a successful push → re-mounts PosTab back to its LIST
  const [reviewMode, setReviewMode] = useState('push');  // 'push' = review + Update now · 'revert' = review + Revert All (#86bbm58f7)
  // Whether the POS tab is on its base list (vs a POS detail / add-type sheet). Reported up by PosTab so
  // the tab bar's connector-wide Advanced button only shows on the base POS list. (#86bbfuajj 3O)
  const [posOnList, setPosOnList] = useState(true);

  // Rights model (rights-acl §2 — bRole is a 0/1 MASTER BIT, there is NO role 2/3; David 2026-08-21):
  //  - bRole 1 = Dividia master → edits EVERYTHING (incl. advanced + identity/config fields).
  //  - bRole 0 + fAdmin 1 = a non-master site-admin → administers devices they hold the ADMIN grant on:
  //    add/remove/replace/discover cameras + edit POS + the local:'edit' fields. Identity + advanced
  //    connector/config fields stay master-only — they're gated on isDividiaMaster DIRECTLY (and via the
  //    access map's local:'hidden'/'readonly'), so opening canManage to fAdmin never leaks them.
  // canManage only gates the ACTION buttons; per-field access:{master,local} (resolveDisp) still decides
  // each field. The backend authorizes every write per-device (modifyOrCreateDevice / modifyOrCreateCamera).
  const isDividiaMaster = isMasterRole(props.bRole);
  const fAdmin = props.fAdmin === 1 || props.fAdmin === true;
  // Per-device (review #86bc63u9g F1): the coarse fAdmin is "ADMIN on ANY device"; cloudapi authorizes
  // each device write on THAT device's grant. canManage is recomputed below once the selected device is
  // known (deviceHasAdmin over the login's deviceRights map), so a device the user only has ACCESS on
  // renders read-only instead of offering edits every save would 403.
  const backendForRights = getBackend();
  const deviceRights = backendForRights && typeof backendForRights.getDeviceRights === 'function' ? backendForRights.getDeviceRights() : null;
  const canManage = isDividiaMaster || (!!form && deviceHasAdmin(deviceRights, form.deviceId, fAdmin));
  const isMobileLandscape = useIsSetupMobileLandscape();   // POS detail confines its scroll (rail + content) in landscape

  const { go, params } = useContext(SetupNavContext);
  // Arrived via a cross-tab jump carrying a device to reselect (Camera → "‹ back to this device")?
  // Capture it once and open that device as soon as the list has loaded.
  const navSelectRef = useRef(params && params.selectDeviceId);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { customers: c, projects: p, devices: d } = await getBackend().getDevices({ token: props.sSess });
      setCustomers(c); setSites(p); setDevices(d);
      return d;   // so a caller (e.g. save) can re-seed the just-saved device from the fresh list
    } catch (e) {
      setError('Could not load devices. ' + (e && e.message ? e.message : ''));
      return null;
    } finally {
      setLoading(false);
    }
  }, [props.sSess]);

  useEffect(() => { load(); }, [load]);

  // Canonical dirty-detection (#86bbm58f7): compare the EFFECTIVE config (cfg folded in, connector keys
  // at their default treated as absent) against pristine — so a seeded field toggled on→off, or any
  // hand-revert, clears dirty instead of sticking Save/Revert on. (configDirty.isDeviceDirty)
  const dirty = isDeviceDirty(form, pristine);
  latestConfigRef.current = form && form.config;
  latestPristineRef.current = pristine && pristine.config;
  const seed = (f, tab = 'general') => {
    // Restore a persisted draft (staged edits that survived a refresh) — loadDraft returns it ONLY when it
    // was made against this same server config, else it discards the stale draft (never clobbers newer
    // state) and we fall back to the fresh snapshot. (#86bbm58f7 refresh-persist)
    const restored = f ? loadDraft(f.deviceId, f) : null;
    // Advanced owns a snapshot of one device's config. Never carry that modal state or snapshot into
    // another selection, where Discard could otherwise restore values from the prior device.
    advSnapshotRef.current = null;
    preDisableConnRef.current = null;   // a captured pre-disable block never carries across device selections/reloads
    setShowAdvanced(false);
    // `tab` defaults to General for a fresh selection; a post-save re-seed passes the current tab so the
    // device stays on the POS tab (its list) instead of jumping back to General. (David 2026-08-30)
    setForm(restored || f); setPristine(f); setError(''); setNotice(''); setActiveTabRaw(tab);
  };

  // Deep-link open: once devices are loaded, if we were told which device to reselect, open it.
  useEffect(() => {
    const did = navSelectRef.current;
    if (!did || !devices.length) return;
    navSelectRef.current = null;
    const d = devices.find((x) => x.sDeviceID === did);
    if (d) { setSelectedId(d.bID); seed(deviceToForm(d)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  // Persist the working draft so staged edits survive a page refresh (#86bbm58f7 refresh-persist).
  // Debounced + keyed by device; saveDraft clears the entry when the form is effectively clean, and the
  // staleness guard (restore only against an unchanged server config) lives in loadDraft.
  useEffect(() => {
    if (!form || !form.deviceId || !pristine) return undefined;
    clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => saveDraft(form.deviceId, pristine, form), 400);
    return () => clearTimeout(draftTimer.current);
  }, [form, pristine]);

  useReportDirty(dirty);
  useSaveSelection('devices', selectedId);
  usePersistedSelection('devices', devices, (d) => d.bID, (d) => {
    setSelectedId(d.bID); seed(deviceToForm(d));
    const restoredSubTab = setupPersistedSubTab('devices');
    if (restoredSubTab) setActiveTabRaw(restoredSubTab);
  });
  const select = async (d) => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId(d.bID); seed(deviceToForm(d));
  };
  const closeDetail = async () => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId(null); seed(null);
  };
  const patch = (p) => { setForm((f) => ({ ...f, ...p })); setNotice(''); };
  const patchCfg = (p) => { setForm((f) => ({ ...f, cfg: { ...f.cfg, ...p } })); setNotice(''); };
  // Edit an UNMANAGED ("Other"/"Misc") config key straight in the blob — writeDevice preserves it and
  // reconcileTypes echoes the device's own type on save (David 2026-08-27: every config item is editable).
  const patchOther = (section, key, value) => { setForm((f) => ({ ...f, config: setEngineKey(f.config, section, key, value) })); setNotice(''); };
  const patchConnOther = (section, key, value) => { setForm((f) => ({ ...f, config: setConnectorKey(f.config, section, key, value) })); setNotice(''); };
  const revert = () => { if (pristine) clearDraft(pristine.deviceId); setForm(pristine); setError(''); setNotice(''); };
  // "Revert Changes" (footer) opens the SAME review list as Push — the change list with per-row ↺ plus an
  // explicit "Revert All" — so reverting is reviewable, not a blind wipe. The sheet itself is the deliberate
  // gate (you see exactly what will revert), so no separate confirm dialog. (#86bbm58f7 David)
  const openRevert = () => { setReviewMode('revert'); setShowReview(true); };

  // Advanced overlay (connector-level fields) is a modal sub-editor: edits go straight into the staged
  // bundle via patchConn, but the overlay closes ONLY via its X or an outside-overlay click (never the
  // footer), and closing it with unsaved connector changes offers KEEP / DISCARD. We snapshot the config
  // when it opens so DISCARD can roll the connector edits back to what they were. (#86bbm58f7 piece 2)
  const openAdvanced = useCallback(() => {
    advSnapshotRef.current = latestConfigRef.current ? JSON.parse(JSON.stringify(latestConfigRef.current)) : null;
    setShowAdvanced(true);
  }, []);
  const closeAdvanced = useCallback(async () => {
    const snap = advSnapshotRef.current;
    const cur = latestConfigRef.current;
    // Prompt only when the overlay actually changed the connector (canon ignores seeded-default no-ops).
    if (snap && cur && !canonConfigEqual(cur, snap)) {
      const discard = await askConfirm({
        title: 'Discard Advanced changes?',
        message: 'You have unsaved Advanced settings. Discard them, or keep them staged?',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep',
        danger: true,
      });
      if (discard) {
        const restore = JSON.parse(JSON.stringify(snap));
        setForm((f) => (f ? { ...f, config: restore, conn: readConnector(restore) } : f));
      }
    }
    advSnapshotRef.current = null;
    setShowAdvanced(false);
  }, []);
  const toggleAdvanced = useCallback(() => {
    if (showAdvanced) closeAdvanced(); else openAdvanced();
  }, [showAdvanced, openAdvanced, closeAdvanced]);

  // Roll form.config back to a snapshot — the POS staged-draft DISCARD path (#86bbm58f7 piece 3).
  const restoreConfig = useCallback((config) => {
    setForm((f) => (f ? { ...f, config, conn: readConnector(config) } : f));
    setNotice('');
  }, []);

  // Camera DISCOVERY (subtask #86bbm53r2). Cloud's ONLY "add a camera" path: the Discover sheet
  // reads the device's ONVIF scan (via cloudapi → backend /ctrl) and STAGES chosen cameras as new
  // [camN] sections into form.config — exactly like addPos stages a POS lane — so they show in the
  // "Push N Changes to Device" review and commit in one conf_rev bump. Gated on the cloud capability
  // AND the device-ADMIN edit gate (canManage); the button lives on the General tab's camera roster,
  // never in the Config form body (David 2026-08-26).
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null);   // camN whose stream the discover sheet is repointing (null = normal add)
  const productConfig = getProductConfig();
  const selectedDeviceClass = classifyDevice(form && form.config);
  // Discover / Remove / Change stream all STAGE [camN] add/drop/repoint into form.config, pushed via
  // POST /ve/device. Like POS lanes, a device ADMIN (canManage) may do all three: cloudapi's local-admin
  // authorizer (deviceConfig.js localCameraChangeAllowed) accepts a whole-section removal, a new camN
  // carrying EXACTLY the keys addDiscoveredCamera stages, and a re-point of only rec_url/live_url(+make/model)
  // — keep configParser's staged keys and that server allowlist in lockstep (#86bc63u9g).
  // A non-master's pushes are authorized only on the MODERN dragon envelope (files.dragon.camN), so a
  // legacy [cameraN] device keeps these master-only (a local Remove there would 403).
  const canStageCameras = canManage && (isDividiaMaster
    || (isModernFormat(form && form.config) && selectedDeviceClass.pill === 'DRAGON'));
  const canUseDiscovery = canStageCameras && selectedDeviceClass.pill === 'DRAGON'
    && !!(productConfig.setup && productConfig.setup.camera && productConfig.setup.camera.canDiscover);
  // Dragon accepts cam1..cam64. Keep stream replacement available at capacity, but never offer an add
  // flow that would stage cam65, which the engine rejects.
  const canDiscover = canUseDiscovery && nextCameraNumber(form && form.config, selectedDeviceClass.family, pristine && pristine.config) != null;
  // Selecting a device (or a refresh) NEVER auto-opens Discover — it opens ONLY on the explicit "Discover
  // cameras" click. (The prior per-device `open` persistence auto-reopened it on any device-select, which
  // read as an unwanted auto-scan.) The staged SELECTIONS still persist per-device and restore when YOU
  // reopen the sheet — so a refresh-while-open loses nothing but the auto-open. (David 2026-08-27)
  useEffect(() => {
    setDiscoverOpen(false); setReplaceTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form && form.deviceId]);
  // Explicit close (Cancel / ×) or a commit discards the draft; only a refresh leaves it to restore.
  const closeDiscover = () => { if (form && form.deviceId) clearDiscoverDraft(form.deviceId); setReplaceTarget(null); setDiscoverOpen(false); };
  const stageDiscovered = (selections) => {
    if (!selections || !selections.length) { closeDiscover(); return; }
    const requested = selections.map((sel) => ({ name: sel.name, recUrl: sel.recUrl, liveUrl: sel.liveUrl, make: sel.make, model: sel.model }));
    const staged = addDiscoveredCameras(form.config, detectFamily(form.config), requested, pristine && pristine.config);
    if (staged.cams.length) setForm((f) => (f ? { ...f, config: staged.config } : f));
    const added = staged.cams.length;
    const parts = [`${added} camera${added === 1 ? '' : 's'} added${staged.skipped || staged.noStream ? '.' : ' — review and Push changes to send them to the device.'}`];
    if (staged.noStream) parts.push(`${staged.noStream} not added because ${staged.noStream === 1 ? 'it has' : 'they have'} no RTSP stream.`);
    if (staged.skipped) parts.push(`${staged.skipped} not added because Dragon supports camera numbers through 64.`);
    setNotice(parts.join(' '));
    closeDiscover();
  };

  // Roster REMOVE — stage dropping a configured camera's [camN] into the batch (confirm-guarded). Nothing
  // is sent until "Push N Changes"; configDirty shows it as a "Camera removed" row with per-row revert.
  const removeCameraStaged = async (num, name) => {
    const ok = await askConfirm({
      title: 'Remove camera?',
      message: `Remove "${name || 'Camera ' + num}" from this device? It stops recording once you Push changes. This only stages the removal — review and Push to apply.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    setForm((f) => (f ? { ...f, config: removeCamera(f.config, num, detectFamily(f.config)) } : f));
    setNotice('Camera removed — review and Push changes to send it to the device.');
  };

  // Roster CHANGE STREAM — open the discover sheet in REPLACE mode for this camera.
  const openReplace = (num) => { setReplaceTarget(num); setDiscoverOpen(true); };

  // The discover sheet's commit. In replace mode: apply the ONE chosen stream to the target camera
  // (confirm-guarded, settings preserved). Otherwise: stage the selections as new cameras.
  const onDiscoverStage = async (selections) => {
    const target = replaceTarget;
    if (target != null) {
      const sel = (selections && selections[0]) || null;
      if (sel && !isStreamUrl(String(sel.recUrl || '').trim())) {
        setNotice(`"${sel.name}" has no RTSP stream — Camera ${target} was not changed.`);
      } else if (sel) {
        const ok = await askConfirm({
          title: 'Replace stream?',
          message: `Point Camera ${target} at "${sel.name}" (${sel.ip})? Its recording + live streams change once you Push; the camera's other settings (name, motion…) are kept.`,
          confirmLabel: 'Replace stream',
        });
        if (!ok) { closeDiscover(); return; }
        setForm((f) => (f ? { ...f, config: replaceCameraStream(f.config, target, { recUrl: sel.recUrl, liveUrl: sel.liveUrl, make: sel.make, model: sel.model }, detectFamily(f.config)) } : f));
        setNotice(`Camera ${target} stream updated — review and Push changes to apply.`);
      }
      closeDiscover();
      return;
    }
    stageDiscovered(selections);
  };

  // Edit one field. A scale-store field (Scale Watcher sensing/tuning/antitheft) writes to
  // [scaleN]; everything else to [posN]. Both are keys under config.files.connector.
  const patchPos = useCallback((posNum, key, value) => {
    setForm((f) => {
      const config = JSON.parse(JSON.stringify(f.config));
      const conn = config.files && config.files.connector;
      if (!conn) return f;
      const posSec = conn['pos' + posNum];
      if (!posSec) return f;
      const schema = POS_TYPES[Number(posSec.type)];
      const target = posFieldStore(schema, key) === 'scale' ? conn['scale' + posNum] : posSec;
      if (!target) return f;   // [scaleN] absent (scale sensing disabled) — nothing to write
      target[key] = posValStr(value);
      return { ...f, config };
    });
    setNotice('');
  }, []);

  const addPos = useCallback((typeNum) => {
    const schema = POS_TYPES[typeNum];
    if (!schema) return null;
    // Compute the new posN from the LATEST config BEFORE setForm (a functional updater runs deferred, so
    // assigning newNum inside it can't be returned reliably) — the caller needs newNum synchronously to
    // open the new lane. (#86bbfuajj)
    const posNums = (cfg) => {
      const conn = (cfg && cfg.files && cfg.files.connector) || {};
      return Object.keys(conn).filter((k) => /^pos\d+$/.test(k)).map((k) => Number(k.slice(3)));
    };
    // Next posN = one past the highest across BOTH the working config AND the pristine baseline, so a
    // number freed by deleting a lane (gone from form but still in pristine) is NOT reused — reusing it
    // would let the new seed clobber that number's pristine section on the dual-seed below. (review #1)
    const nums = [...posNums(latestConfigRef.current), ...posNums(latestPristineRef.current)];
    const newNum = nums.length ? Math.max(...nums) + 1 : 1;
    // Seed [posN] with the pos-store defaults only. A Scale Watcher's [scaleN] is created separately by
    // the scale Enable toggle (mirrors connector.html: a new lane starts with sensing OFF), so
    // scale-store fields are NOT written here.
    // `existing` is the working config BEFORE this lane is added, so nextFreePort sees the current ports and
    // hands back the lowest free one in the type's range (port-less types get null → keep the field default).
    const seedSec = (existing) => {
      const sec = { type: String(typeNum) };
      const autoPort = nextFreePort(schema, existing);
      schema.fields.forEach((field) => {
        if (posFieldStore(schema, field.key) === 'scale') return;
        if (field.key === 'tcp_port' && autoPort != null) { sec[field.key] = posValStr(autoPort); return; }
        sec[field.key] = posValStr(field.default);
      });
      return sec;
    };
    const withPos = (cfg) => {
      const config = JSON.parse(JSON.stringify(cfg || {}));
      if (!config.files) config.files = {};
      if (!config.files.connector) config.files.connector = {};
      config.files.connector['pos' + newNum] = seedSec(config);   // pass the pre-add config so the port is free
      return config;
    };
    // Add the new lane to the WORKING config only — NOT pristine. A freshly-added POS is a staged draft:
    // it makes the device dirty (so it shows in the review) and commits only when the user leaves the POS
    // detail with "Keep" — "Discard" rolls it back out via the on-open snapshot (PosTab.leavePos).
    // (#86bbm58f7 piece 3 — replaces the old dual-seed-into-pristine hack.)
    setForm((f) => ({ ...f, config: withPos(f.config) }));
    setNotice('');
    return newNum;
  }, []);

  const deletePos = useCallback(async (posNum) => {
    const ok = await askConfirm({
      title: 'Delete POS source?',
      message: 'This POS source and all its configuration will be removed.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return false;
    setForm((f) => {
      const config = JSON.parse(JSON.stringify(f.config));
      const conn = config.files && config.files.connector;
      if (conn) { delete conn['pos' + posNum]; delete conn['scale' + posNum]; }   // drop both sections
      return { ...f, config };
    });
    setNotice('');
    return true;
  }, []);

  // Edit a connector-LEVEL field (files.connector.<section> — the Advanced overlay's global/rpc/
  // dragon/cloud settings, NOT a posN/scaleN lane). Mirrors patchPos: writes the raw config and
  // re-derives form.conn so the overlay reflects the change immediately. `patch` is keyed by the
  // field's form name (matching ConfigToggle's patchCfg({[f.form]: value}) contract); a read-only
  // fact is never written even if one slips in.
  const patchConn = useCallback((patch) => {
    setForm((f) => {
      const config = JSON.parse(JSON.stringify(f.config));
      if (!config.files) config.files = {};
      if (!config.files.connector) config.files.connector = {};
      const conn = config.files.connector;
      Object.keys(patch).forEach((formKey) => {
        const fld = connectorFields().find((d) => d.form === formKey);
        if (!fld || cfgDisp(fld.section, fld.key, true) !== 'edit') return;   // never write a field the map locks even for master
        if (!conn[fld.section]) conn[fld.section] = {};
        conn[fld.section][fld.key] = posValStr(patch[formKey]);
      });
      return { ...f, config, conn: readConnector(config) };
    });
    setNotice('');
  }, []);

  // Scale-sensing Enable = the presence of a [scaleN] section (its presence starts the
  // connector's scale-reader thread). On enable, seed [scaleN] with the scale defaults; on
  // disable, remove it.
  const setScaleEnabled = useCallback((posNum, on) => {
    setForm((f) => {
      const config = JSON.parse(JSON.stringify(f.config));
      const conn = config.files && config.files.connector;
      if (!conn || !conn['pos' + posNum]) return f;
      const schema = POS_TYPES[Number(conn['pos' + posNum].type)];
      if (on) {
        if (!conn['scale' + posNum]) conn['scale' + posNum] = scaleSeed(schema);
      } else {
        delete conn['scale' + posNum];
      }
      return { ...f, config };
    });
    setNotice('');
  }, []);

  // Enable/Disable POS = the presence of the whole files.connector block (86bc1nuf9). ENABLE seeds the
  // outer shell (global/rpc/dragon/cloud, no posN) so the POS tab's list/add/advanced appear and the FIRST
  // lane can be authored from the cloud (the tab is content-gated + the only "add lane" control is inside
  // it, so without a shell the first lane can never be created). DISABLE removes the entire block; that
  // drops any POS lanes, so we confirm first when lanes exist. Master-only (gated at the call site) — it
  // authors connector-wide config, like the Advanced overlay — and disabling closes Advanced, which would
  // otherwise linger open onto a block that no longer exists.
  const setConnectorEnabled = useCallback(async (on) => {
    if (on) {
      setForm((f) => {
        if (!f || hasConnectorBlock(f.config)) return f;    // already enabled — never clobber a real block
        // Re-enabling undoes a STAGED (un-pushed) disable: restore the exact block that was toggled off
        // (captured in preDisableConnRef), preferring it over the pristine block, so BOTH real POS lanes and
        // un-pushed connector edits survive a disable then re-enable. A device that genuinely never had a
        // connector (nothing to restore) gets the seeded shell. Object.keys guards an empty {} block, which
        // hasConnectorBlock treats as absent — restoring {} would leave a dead toggle. (86bc1nuf9 review)
        const pconn = latestPristineRef.current && latestPristineRef.current.files && latestPristineRef.current.files.connector;
        const stashed = preDisableConnRef.current;
        // An empty {} stash (shouldn't occur — Disable only runs when a block is present) must not SHADOW a
        // real pristine block, so the emptiness guard is applied to the stash itself, not just the result.
        const prior = (stashed && Object.keys(stashed).length) ? stashed : pconn;
        const config = (prior && Object.keys(prior).length)
          ? { ...f.config, files: { ...f.config.files, connector: JSON.parse(JSON.stringify(prior)) } }
          : enableConnector(f.config);
        return { ...f, config, conn: readConnector(config) };
      });
      // Not cleared here: consuming happens in the deferred updater above, and once enabled the guard blocks a
      // second consume. It is overwritten by the next Disable and reset on device change (seed).
      setNotice('POS enabled — add POS sources below, then Push changes to the device.');
      return;
    }
    const posNums = listConnectorPos(latestConfigRef.current);
    if (posNums.length) {
      const ok = await askConfirm({
        title: 'Disable POS?',
        message: `This removes the POS connector and all ${posNums.length} POS source${posNums.length === 1 ? '' : 's'} on this device — they stop capturing once you Push changes.`,
        confirmLabel: 'Disable POS',
        danger: true,
      });
      if (!ok) return;
    }
    // Stash the block being removed so a later Re-enable can undo this staged disable (restores lanes + edits).
    const cur = latestConfigRef.current;
    preDisableConnRef.current = (cur && cur.files && cur.files.connector) || null;
    setShowAdvanced(false);
    setForm((f) => {
      if (!f) return f;
      const config = disableConnector(f.config);
      return { ...f, config, conn: readConnector(config) };
    });
    setNotice('POS disabled — Push changes to the device to apply.');
  }, []);

  // Changing the customer re-filters the Site picker and moves the site into that customer (its
  // first site, or none) — isite's cascade.
  const changeCustomer = (customerId) => {
    const cid = Number(customerId);
    const forCustomer = sites.filter((s) => s.bCustomerID === cid);
    patch({ customerId: cid || '', projectId: forCustomer.length ? forCustomer[0].bID : '' });
  };

  // Validation shared by the "Push Changes to Device" gate and the actual save. Returns an error string
  // (shown in the detail) or '' when the staged config is safe to push. (#86bbm58f7 piece 4)
  const validate = () => {
    if (!form.name.trim()) return 'Name is required.';
    if (!form.customerId) return 'A customer is required.';
    if (!form.projectId) return 'A site is required.';
    // POS required-field validation, schema-driven: a lane with a `required` field left empty can't be
    // pushed (e.g. a PSCL lane needs RabbitMQ Host — the connector won't start a PSCL reader without it).
    const conn = form.config && form.config.files && form.config.files.connector;
    if (conn) {
      // Compare ports against the pristine baseline so an UNCHANGED legacy port predating the ranges isn't
      // retro-blocked — only a new/changed port is range-checked (cloudapi grandfathers the same way). Every
      // finite port still counts toward uniqueness. (#86bbpgbdz)
      const pconn = (pristine && pristine.config && pristine.config.files && pristine.config.files.connector) || {};
      const seenPorts = new Map();   // port number -> { label, changed } — dup blocks only if a colliding lane changed
      for (const key of Object.keys(conn)) {
        const m = /^pos(\d+)$/.exec(key);
        if (!m) continue;
        const sec = conn[key];
        const sch = POS_TYPES[Number(sec.type)];
        if (!sch) continue;
        const label = sec.name || ('POS ' + m[1]);
        const missing = (sch.fields || []).find((f) => f.required && posFieldStore(sch, f.key) !== 'scale'
          && !String(sec[f.key] == null ? '' : sec[f.key]).trim());
        if (missing) return `${sch.shortName} POS "${label}": ${missing.label} is required.`;
        // Port range + uniqueness (#86bbpgbdz) — port-bearing types only (LPR / Scale Watcher). A port outside
        // the type's range, or shared with another POS on the device, blocks the push; cloudapi enforces the same.
        const range = portRangeOf(sch);
        if (range) {
          const [min, max] = range;
          const port = Number(sec.tcp_port);
          const portChanged = !pconn[key] || String(pconn[key].tcp_port) !== String(sec.tcp_port);
          if (portChanged) {
            if (!Number.isFinite(port)) return `${sch.shortName} POS "${label}": a listen port is required.`;
            if (!/^\d+$/.test(String(sec.tcp_port).trim())) return `${sch.shortName} POS "${label}": port must be a whole number.`;
            if (port < min || port > max) return `${sch.shortName} POS "${label}": port must be between ${min} and ${max}.`;
          }
          if (Number.isFinite(port)) {
            // A duplicate blocks the push only if THIS push changed one of the colliding lanes — a pre-existing
            // duplicate (two legacy lanes on the old 6501 default) is grandfathered so it can't lock an unrelated
            // edit, mirroring the range grandfathering + cloudapi. (#86bbpgbdz)
            const prev = seenPorts.get(port);
            if (prev && (portChanged || prev.changed)) {
              return `Port ${port} is used by two POS sources ("${prev.label}" and "${label}"). Each POS needs its own port.`;
            }
            if (!prev) seenPorts.set(port, { label, changed: portChanged });
          }
        }
      }
    }
    return '';
  };

  const save = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setBusy(true); setError(''); setNotice('');
    const tab = activeTab;                    // the tab this push came from (POS) — return to it on success
    const savedTo = form.name || form.deviceId;
    try {
      // Merge device-level config edits back into the blob — writeDevice writes the family's writable
      // device keys (present-or-not) while preserving unknown keys + untouched sections. An
      // Unknown/empty device (cfg=null) round-trips the blob unchanged, exactly as before.
      // conf_rev is owned server-side now: cloudapi modifyDeviceConfig does an atomic compare-and-increment
      // against the live DB value, per-file (connector AND dragon). The client no longer bumps it — a
      // client-held rev can be stale.
      const built = form.cfg ? writeDevice(form.config, detectFamily(form.config), form.cfg) : form.config;
      // TYPE-ECHO the payload to the device's own JSON types before it hits the DB — the device-safety net.
      // Present fields keep the exact type the device sent (number/string/bool); nothing can be mis-typed,
      // and when the device flips bools to true/false this follows for free. (#86bbm58f7)
      const config = pristine ? reconcileTypes(built, pristine.config) : built;
      const { ok, error } = await getBackend().saveDevice({
        id: form.id,
        name: form.name,
        customerId: Number(form.customerId),
        projectId: Number(form.projectId),
        deviceId: form.deviceId,
        config,
      }, { token: props.sSess });
      if (!ok) throw new Error(error || 'Save was rejected.');
      clearDraft(form.deviceId);
      // SUCCESS: reload for freshness, then RE-SEED the same device so it stays open on the SAME tab (the
      // POS list) — never deselect to a blank pane. The re-seed makes it clean (form === pristine → not
      // dirty), so the push footer clears. (David 2026-08-30)
      const fresh = await load();
      const saved = (fresh || []).find((x) => x.bID === form.id);
      if (saved) seed(deviceToForm(saved), tab);
      else { setSelectedId(null); setForm(null); setPristine(null); }   // device vanished server-side → list fallback
      // Reset the POS tab back to its LIST. The re-seed clears the DEVICE dirty state, but PosTab holds its
      // OWN state (which POS is open + whether it's a not-yet-added "new" source). Without this it stays on
      // the just-pushed POS detail and going back wrongly prompts "Discard new POS source?". (David 2026-08-30)
      setPosResetToken((t) => t + 1);
      setAlert({ type: 'info', title: 'Success!', message: 'Your changes were pushed to ' + savedTo + '.' });
    } catch (e) {
      // ERROR: do NOT mutate app state (form/pristine untouched → nothing is silently applied) and do NOT
      // redirect — stay on the detail so the reason shows and the user can retry. (David 2026-08-30)
      const reason = serverErrorText(e, e && e.message ? e.message : 'You may not have permission.');   // server's own text first (F3)
      setError(reason);
      setAlert({ type: 'error', title: 'Push failed', message: 'Could not push your changes to the device. ' + reason });
    } finally { setBusy(false); }
  };

  // "Push Changes to Device" review (#86bbm58f7 piece 4): the human-readable diff the push will send, so
  // the user confirms they didn't change something unintended. Org (Customer/Site) changes are labelled
  // here where the id→name maps live; config changes come from configChanges. The push itself is the SAME
  // single saveDevice call — one conf_rev bump per changed file, server-owned — batching just gates it
  // behind one explicit, reviewed action instead of a save per edit.
  const nameOfCustomer = (id) => { const c = customers.find((x) => x.bID === Number(id)); return c ? c.sName : (id ? String(id) : '—'); };
  const nameOfSite = (id) => { const s = sites.find((x) => x.bID === Number(id)); return s ? s.sName : (id ? String(id) : '—'); };
  const reviewChanges = () => {
    if (!form || !pristine) return [];
    const org = [];
    if (Number(form.customerId) !== Number(pristine.customerId)) org.push({ path: 'customerId', label: 'Customer', from: nameOfCustomer(pristine.customerId), to: nameOfCustomer(form.customerId) });
    if (Number(form.projectId) !== Number(pristine.projectId)) org.push({ path: 'projectId', label: 'Site', from: nameOfSite(pristine.projectId), to: nameOfSite(form.projectId) });
    return [...org, ...configChanges(pristine, form)];
  };
  const openReview = () => { const err = validate(); if (err) { setError(err); return; } setError(''); setReviewMode('push'); setShowReview(true); };
  const reviewApply = async () => { setShowReview(false); await save(); };
  const reviewDiscard = () => { revert(); setShowReview(false); };
  // Per-row revert: drop ONE staged change from the review by its path, keeping the rest of the batch. If
  // that was the last change, close the review (nothing left to push). (#86bbm58f7 editable diff)
  const revertRow = (path) => {
    const next = revertChange(form, pristine, path);
    setForm(next);
    if (!isDeviceDirty(next, pristine)) setShowReview(false);
  };
  // Staged-change count for the footer button ("Push N changes to Device") — makes the batch model legible:
  // changes ACCUMULATE and Push sends them all at once in ONE conf_rev bump, so users read it as a
  // send-everything action rather than a per-edit save. (#86bbm58f7 option B, David 2026-08-26)
  const pushCount = dirty ? reviewChanges().length : 0;

  const sitesForCustomer = form ? sites.filter((s) => s.bCustomerID === Number(form.customerId)) : [];
  const fam = form ? familyBadge(form.config) : null;

  // Tab model, computed from the selected device's config. General is always present (shared by both
  // families); Config/Misc appear only when this family/device actually has content (capability-gated,
  // mirroring how the Camera screen hides the LPR tab). Config is FEATURE-GROUPED — each subsystem's
  // switch(es) with the read-only facts it uses right beneath (buildDeviceGroups). Misc = the unmanaged
  // passthrough keys not tied to any switch (videostore, thumb_*).
  const cfgFamily = form && form.cfg ? detectFamily(form.config) : null;
  const dFields = cfgFamily ? deviceFields(cfgFamily) : [];
  const cfgOther = cfgFamily ? otherDeviceKeys(form.config, cfgFamily) : [];
  const llmOther = cfgOther.filter((o) => o.section === 'llm');   // scene/objects/scene_prompt → the LLM group
  const miscOther = cfgOther.filter((o) => o.section !== 'llm');  // everything else → Misc
  const configGroups = cfgFamily ? buildDeviceGroups(cfgFamily, dFields, llmOther, isDividiaMaster, form.cfg.has) : [];
  // Connector (POS) — modern JSON only; shows as its own tab (matches Java dview's POS tab)
  const hasConnector = form && form.conn != null;   // modern/dragon device (readConnector returns null on legacy/INI)
  const connectorPresent = hasConnector && hasConnectorBlock(form.config);   // files.connector block exists ⇒ POS is enabled
  const connOther = hasConnector ? otherConnectorKeys(form.config) : [];
  const connPosList = hasConnector ? listConnectorPos(form.config) : [];
  const connGroups = hasConnector ? buildConnectorGroups(form.conn, connOther, isDividiaMaster) : [];
  const deviceTabs = [{ id: 'general', label: 'General' }];
  if (configGroups.length) deviceTabs.push({ id: 'config', label: 'Config' });
  // POS tab: whenever the block has content (the existing gate), OR a master is on a dragon device and could
  // ENABLE it — the Enable POS toggle lives in this tab, so it must be reachable before the block exists.
  // Gated on isDividiaMaster (NOT the broader canManage), matching the connector-wide Advanced control: a
  // non-master can't author the connector shell, and a non-master on a POS-less device sees no POS tab.
  // (86bc1nuf9)
  if (connGroups.length || connPosList.length || (hasConnector && isDividiaMaster)) deviceTabs.push({ id: 'pos', label: 'POS' });
  if (miscOther.length) deviceTabs.push({ id: 'misc', label: 'Misc' });
  const activeDeviceTab = deviceTabs.some((t) => t.id === activeTab) ? activeTab : 'general';

  // Match the device against what the CARD actually shows: name, device ID, and its org sub-label —
  // which is "<customer> · <site>" when assigned or the literal "Unassigned" when it has no customer
  // (bCustomerID null/0). So typing "unassigned" finds the unassigned devices (a master's cue for
  // what still needs assigning), and site names match too. Case-insensitive. (#86bbm6j8w)
  const orgLabelOf = (d) => ((d.bCustomerID && d.sCustomerName) ? orgSubLabel(d.sCustomerName, d.sProjectName) : 'Unassigned');
  const q = filter.toLowerCase();
  const filtered = devices.filter((d) =>
    !filter ||
    (d.sName || '').toLowerCase().includes(q) ||
    (d.sDeviceID || '').toLowerCase().includes(q) ||
    orgLabelOf(d).toLowerCase().includes(q))
    // ordered by device name (an unnamed device falls back to its ID, which is its display name)
    .sort((a, b) => (a.sName || a.sDeviceID || '').toLowerCase()
      .localeCompare((b.sName || b.sDeviceID || '').toLowerCase()));
  // mobile-landscape split shows list + detail together → auto-select the first device on first load
  // so the detail pane isn't empty (landscape-only; portrait keeps the list-first drill).
  useAutoSelectFirstInLandscape(filtered, selectedId !== null || !!(params && params.selectDeviceId) || setupPersistedSelection('devices') != null, select);

  const detailOpen = form !== null;
  const selDev = devices.find((d) => d.bID === selectedId);
  const detailTitle = selDev ? (selDev.sName || selDev.sDeviceID || 'Device') : '';

  // Jump to the Camera tab scoped to THIS device (Camera shows only its cams + a back link that
  // returns here). Uses the saved device name for the breadcrumb, not the unsaved Name field.
  const camScope = () => ({
    scopeDeviceId: form.deviceId,
    scopeDeviceName: (selDev && (selDev.sName || selDev.sDeviceID)) || (form && form.deviceId),
  });
  // "View all" → the Camera tab scoped to this device, FIRST camera (clear any per-camera selection so
  // it doesn't restore a previously-focused one). Keeps the pre-existing behaviour David wanted retained.
  const viewCameras = () => { saveSetupSelection('cameras', null); go('cameras', camScope()); };
  // A roster card → the Camera tab scoped to this device, FOCUSED on that camera: seed the persisted
  // selection the Camera screen restores on mount (bCamera === the config camera number).
  const viewCamera = (num) => {
    saveSetupSelection('cameras', { deviceId: form.deviceId, bCamera: num });
    go('cameras', { ...camScope(), focusCamera: num });
  };

  return (
    <>
    <SetupDrill
      detailOpen={detailOpen}
      backLabel="Devices"
      title={detailTitle}
      onBack={closeDetail}
      bare={activeDeviceTab === 'pos' && !posOnList}
      /* Confined scroll (SetupDrill fillBody). POS list: the Add/Advanced action row pins while only the
         card list scrolls (base POS list only). GENERAL: the assignment fields + identity facts pin while
         only the camera roster grid scrolls — at every breakpoint it overflows (David 2026-08-27). */
      fillBody={detailOpen && (
        activeDeviceTab === 'general' ||
        (activeDeviceTab === 'pos' && (posOnList || isMobileLandscape))
      )}
      headingNav={deviceTabs.length > 0 && !(activeDeviceTab === 'pos' && !posOnList) ? (
        <div className="setup-tabbar">
          {deviceTabs.map((t) => (
            <button key={t.id}
              className={'setup-tabbar__tab' + (activeDeviceTab === t.id ? ' setup-tabbar__tab--active' : '')}
              onClick={() => setActiveTab(t.id)}>{t.label}</button>
          ))}
          {/* Advanced (connector-wide settings) applies to ALL POS, so it belongs only on the base POS
              list — hidden while adding/modifying a single POS (posOnList). MASTER-ONLY (#86bbm6j8w):
              every connector field is local:hidden, so a local admin's Advanced would open to nothing —
              so the button itself is hidden for non-masters. */}
          {activeDeviceTab === 'pos' && posOnList && isDividiaMaster && connectorPresent ? (
            <button type="button"
              className={'setup-tabbar__action' + (showAdvanced ? ' setup-tabbar__action--active' : '')}
              onClick={toggleAdvanced}>Advanced</button>
          ) : null}
        </div>
      ) : null}
      accessory={detailOpen ? (
        <>
          <Badge color={fam.color}>{fam.label}</Badge>
          <Badge color={form.stale ? '#b08800' : form.online ? '#2e7d32' : '#555'}>
            {form.stale ? 'Stale — ' + fmtAgeDays(eventAgeDays(form.lastEvent)) : form.online ? 'Online' : 'Offline'}
          </Badge>
        </>
      ) : null}
      list={(
        <SetupListPanel
          searchFirst
          filter={filter}
          setFilter={setFilter}
          filterPlaceholder="Filter devices…"
          loading={loading}
          empty={!filtered.length}
          emptyText="No devices."
        >
          {filtered.map((d) => {
            const cfg = d.sConfig && typeof d.sConfig === 'object' ? d.sConfig : {};
            const cls = classifyDevice(cfg);
            const online = d.fOnline === 1 || d.fOnline === true;
            const stale = online && isEventStale(d.sLastEventTS);
            const ageDays = eventAgeDays(d.sLastEventTS);
            const dotClass = stale ? 'setup-status-dot--stale'
              : online ? 'setup-status-dot--online' : 'setup-status-dot--offline';
            const dotTitle = stale ? 'Online — no events in ' + fmtAgeDays(ageDays)
              : online ? 'Online' : 'Offline';
            return (
              <ListRow
                key={d.bID}
                active={selectedId === d.bID}
                onClick={() => select(d)}
                name={d.sName || d.sDeviceID}
                sub={(d.bCustomerID && d.sCustomerName)
                  ? orgSubLabel(d.sCustomerName, d.sProjectName)
                  : 'Unassigned'}
                accessories={<>
                  <span className={'setup-pill setup-pill--' + cls.pillClass}>{cls.pill}</span>
                  <span className={'setup-status-dot ' + dotClass} title={dotTitle} />
                </>}
                meta={d.sLastEventTS ? fmtLastEvent(d.sLastEventTS) : null}
              />
            );
          })}
        </SetupListPanel>
      )}
      /* Footer renders ONLY when there are staged changes — a clean device shows no action bar at all
         (an always-visible disabled "Push" read as "you have unsaved changes" = confusion). Both buttons
         appear together the moment the device is dirty; `dirty` stays true through the save (form/pristine
         only clear on success), so the "Saving…" state still shows. (#86bbm58f7 David) */
      actions={detailOpen && dirty ? (
        <>
          <div className="setup-actions__spacer" />
          <button className="setup-btn--secondary" disabled={busy} onClick={openRevert}>Revert Changes</button>
          <button className="setup-btn--save" disabled={busy} onClick={openReview}>
            {busy ? 'Saving…' : (pushCount > 0
              ? ('Push ' + pushCount + (pushCount === 1 ? ' Change' : ' Changes') + ' to Device')
              : 'Push Changes to Device')}
          </button>
        </>
      ) : null}
    >
      {detailOpen ? (
        <>
          {/* Not ADMIN on THIS device → every Field read-only (F1); tab/section navigation stays live. */}
          <SetupReadOnlyContext.Provider value={!canManage}>
          <div className="setup-tabbar__body">
            {activeDeviceTab === 'general' && (
              <GeneralTab form={form} patch={patch} patchConn={patchConn} changeCustomer={changeCustomer}
                customers={customers} sites={sitesForCustomer} isMaster={isDividiaMaster}
                onViewCameras={viewCameras} onViewCamera={viewCamera}
                onDiscover={canDiscover ? () => { setReplaceTarget(null); setDiscoverOpen(true); } : null}
                onRemoveCamera={canStageCameras ? removeCameraStaged : null}
                onReplaceCamera={canUseDiscovery ? openReplace : null} />
            )}
            {activeDeviceTab === 'config' && (
              <ConfigTab cfg={form.cfg} patchCfg={patchCfg} patchOther={patchOther} groups={configGroups} config={form.config} isMaster={isDividiaMaster} />
            )}
            {activeDeviceTab === 'pos' && (
              <PosTab key={posResetToken} conn={form.conn} groups={connGroups} posList={connPosList} config={form.config} pristineConn={pristine && pristine.config && pristine.config.files && pristine.config.files.connector} canManage={canManage}
                connectorEnabled={connectorPresent} onSetConnectorEnabled={setConnectorEnabled}
                showAdvanced={showAdvanced} onCloseAdvanced={closeAdvanced} onToggleAdvanced={toggleAdvanced} onPosViewChange={setPosOnList}
                onExitDevice={closeDetail} deviceName={detailTitle} deviceId={form.deviceId} token={props.sSess}
                onPatchPos={patchPos} onPatchConn={patchConn} onPatchConnOther={patchConnOther} onAddPos={addPos} onDeletePos={deletePos} onSetScaleEnabled={setScaleEnabled} onRestoreConfig={restoreConfig} isMaster={isDividiaMaster} />
            )}
            {activeDeviceTab === 'misc' && <MiscTab entries={miscOther} patchOther={patchOther} isMaster={isDividiaMaster} />}
          </div>
          </SetupReadOnlyContext.Provider>

          {!isDividiaMaster && !canManage ? (
            <div className="setup-hint setup-hint--spaced">You don&rsquo;t administer this device, so its settings are read-only.</div>
          ) : !isDividiaMaster ? (
            <div className="setup-hint setup-hint--spaced">Fields you can edit are enabled; the rest are read-only for your role.</div>
          ) : null}

          {error ? <div className="setup-error">{error}</div> : null}
          {notice ? <div className="setup-notice">{notice}</div> : null}
        </>
      ) : null}
    </SetupDrill>
    {showReview ? (
      <Sheet className="app-sheet setup-review" title={reviewMode === 'revert' ? 'Revert changes' : 'Push changes to device'} onClose={() => setShowReview(false)}>
        {(() => {
          const changes = reviewChanges();
          return changes.length ? (
            <ul className="setup-review__list">
              {changes.map((c) => (
                <li key={c.path} className="setup-review__item">
                  <span className="setup-review__label">{c.label}</span>
                  <span className="setup-review__end">
                    <span className="setup-review__delta">{c.from} <span className="setup-review__arrow">→</span> {c.to}</span>
                    <button type="button" className="setup-review__revert" title="Revert this change"
                      aria-label={'Revert ' + c.label} onClick={() => revertRow(c.path)}>↺</button>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="setup-review__empty">{reviewMode === 'revert' ? 'No changes to revert.' : 'No changes to push.'}</div>
          );
        })()}
        <div className="setup-review__actions">
          {reviewMode === 'revert' ? (
            <>
              <button type="button" className="setup-btn--secondary" disabled={busy} onClick={() => setShowReview(false)}>Keep changes</button>
              <button type="button" className="setup-btn--danger" disabled={busy} onClick={reviewDiscard}>Revert All</button>
            </>
          ) : (
            <>
              <button type="button" className="setup-btn--secondary" disabled={busy} onClick={reviewDiscard}>Discard all</button>
              <button type="button" className="setup-btn--save" disabled={busy} onClick={reviewApply}>{busy ? 'Applying…' : 'Update now'}</button>
            </>
          )}
        </div>
      </Sheet>
    ) : null}
    {discoverOpen && form ? (
      <DiscoverCamerasSheet
        deviceId={form.deviceId}
        config={form.config}
        pristineConfig={pristine && pristine.config}
        token={props.sSess}
        replaceTarget={replaceTarget}
        onStage={onDiscoverStage}
        onClose={closeDiscover}
      />
    ) : null}
    <AlertModal show={!!alert} title={alert && alert.title} message={alert && alert.message}
      type={alert && alert.type} onClose={() => setAlert(null)} />
    </>
  );
};

// ---- tabs (data-driven from configParser's device schema) ------------------

// GENERAL — org assignment (editable) + the device facts (read-only). Identical for every family,
// so it is the one tab that always shows.
// One roster camera card: a clickable MAIN region (→ the Camera tab focused on it) plus, for a manager,
// a ⋯ menu with the structural actions (Change stream… / Remove). The card can't be a single <button>
// anymore (a menu button can't nest inside it), so it's a div with an inner main button + the menu.
// Destructive/structural actions are confirmed by the parent (askConfirm). (#86bbm53r2 remove/replace)
const RosterCard = ({ cam, onView, onRemove, onReplace }) => {
  const [menu, setMenu] = useState(false);
  const hasMenu = !!(onRemove || onReplace);
  return (
    <div className="setup-cam-roster__card">
      <button type="button" className="setup-cam-roster__card-main" onClick={() => onView(cam.num)}>
        <span className="setup-cam-roster__card-top">
          <span className={'setup-cam-roster__dot setup-status-dot ' + (cam.enabled ? 'setup-status-dot--online' : 'setup-status-dot--offline')} />
          <span className="setup-cam-roster__num">{cam.num}</span>
          <span className="setup-cam-roster__name">{cam.name}</span>
        </span>
        {((cam.type && cam.type !== cam.name) || cam.ip) ? (
          <span className="setup-cam-roster__card-sub">
            {(cam.type && cam.type !== cam.name) ? <span className="setup-cam-roster__card-type">{cam.type}</span> : null}
            {cam.ip ? <span className="setup-cam-roster__card-ip">{cam.ip}</span> : null}
          </span>
        ) : null}
      </button>
      {hasMenu ? (
        <button type="button" className="setup-cam-roster__menu-btn" aria-haspopup="menu" aria-expanded={menu}
          aria-label={`Manage camera ${cam.num}`} onClick={() => setMenu(true)}>⋯</button>
      ) : null}
      {menu ? (
        /* The action menu is the shared Sheet — a bottom sheet on mobile/tablet, a centered modal on
           desktop — matching every other setup menu (was an inline popover, wrong on touch). David 2026-08-27 */
        <Sheet className="app-sheet setup-cam-roster__sheet" title={cam.name || `Camera ${cam.num}`} onClose={() => setMenu(false)}>
          {onReplace ? (
            <button type="button" className="setup-cam-roster__menu-item"
              onClick={() => { setMenu(false); onReplace(cam.num); }}>
              <span className="setup-cam-roster__menu-ic" aria-hidden="true">⇄</span>Change stream…
            </button>
          ) : null}
          {onRemove ? (
            <button type="button" className="setup-cam-roster__menu-item setup-cam-roster__menu-item--danger"
              onClick={() => { setMenu(false); onRemove(cam.num, cam.name); }}>
              <span className="setup-cam-roster__menu-ic" aria-hidden="true">⊖</span>Remove
            </button>
          ) : null}
        </Sheet>
      ) : null}
    </div>
  );
};

const GeneralTab = ({ form, patch, patchConn, changeCustomer, customers, sites, isMaster, onViewCameras, onViewCamera, onDiscover, onRemoveCamera, onReplaceCamera }) => {
  // The camera count is DERIVED FROM THE CONFIG (David 2026-07-23: the config dictates the count, not
  // JSDevice.bCameras which is stale device-reported cruft). Shown as "N of M enabled" — M = authorized
  // (# of config cam sections), N = how many carry enabled=1. Both come from form.config.
  const counts = cameraCounts(form.config);
  // Config-driven camera roster (num · name · enabled) — a glanceable preview grid below the facts so
  // you can see this device's cameras without switching tabs (David #86bbfuajj). bCamera === the config
  // camera number, so a card hands the Camera tab that number to focus.
  const camRoster = listConfigCameras(form.config).map((n) => {
    const s = cameraSection(form.config, n) || {};
    const nm = (s.name != null ? String(s.name) : '').trim();
    return {
      num: n, name: nm || `Camera ${n}`, enabled: boolFromCfg(s.enabled),
      type: cameraType(s.make, s.model),   // discovered hardware make/model (blank for pre-Discover cams)
      ip: cameraIpFromSection(s),           // LAN host from the RTSP URL
    };
  });
  // "Device Info" disclosure (David 2026-08-27): the read-only device facts collapse behind a caret +
  // label + rule on ONE row. ALWAYS starts collapsed — reset whenever the device changes — so the camera
  // roster (the flex:1 scroller below) gets the freed vertical room until you deliberately open it.
  const [factsOpen, setFactsOpen] = useState(false);
  useEffect(() => { setFactsOpen(false); }, [form.deviceId]);
  return (
  <div className="setup-general">
    {/* 3-across at landscape (Name · Customer · Site) — uniform row, no odd single-then-double.
        Stacks to one column in portrait. (No "Assignment" header — the fields are self-evident.) */}
    {/* Every input on this tab is the ONE Field — type picks the widget, access (single source) picks
        hidden/readonly/edit. Name = string, Customer/Site = enum (functionally disabled when there is
        nothing to pick / no customer chosen yet — a functional lock, not an access decision). */}
    <div className="setup-form-3col">
      <Field type="string" label="Name" value={form.name} access={fieldAccess('device.name')} isMaster={isMaster}
        onChange={(v) => patch({ name: v })} placeholder="Device name" maxLength={DEVICE_NAME_MAX} />
      <Field type="enum" label="Customer" value={form.customerId || ''} access={fieldAccess('device.customer')} isMaster={isMaster}
        disabled={customers.length <= 1}
        options={[{ value: '', label: '— None —' }, ...customers.map((c) => ({ value: String(c.bID), label: c.sName }))]}
        onChange={(v) => changeCustomer(v)} />
      <Field type="enum" label="Site" value={form.projectId || ''} access={fieldAccess('device.site')} isMaster={isMaster}
        disabled={!form.customerId}
        options={[{ value: '', label: '— None —' }, ...sites.map((s) => ({ value: String(s.bID), label: s.sName }))]}
        onChange={(v) => patch({ projectId: v ? Number(v) : '' })} />
    </div>

    {/* "Device Info" disclosure header - caret + label + rule on one row (replaces the plain divider).
        Toggles the read-only facts below; collapsed by default so the camera roster gets the room. */}
    <button type="button" className={'setup-disclose' + (factsOpen ? ' setup-disclose--open' : '')}
      aria-expanded={factsOpen} onClick={() => setFactsOpen((v) => !v)}>
      <span className="setup-disclose__caret" aria-hidden="true">›</span>
      <span className="setup-disclose__label">Device Info</span>
      <span className="setup-disclose__rule" aria-hidden="true" />
    </button>
    {/* Device identity/status — SAME ONE Field + SAME gate as everything else (default master:edit). Serial
        persists via the connector config (patchConn); Device ID / Backend ID / IP persist in the device
        record; Last Event + Cameras are device-reported/derived. Lock any of these for a role by adding its
        'device.<field>' key to setupAccess — one line — never by special-casing the render. #86bbfuajj */}
    {factsOpen ? (
    <div className="setup-facts">
      {form.conn ? (
        <Field type="string" label="Serial" value={form.conn.connSerial != null ? String(form.conn.connSerial) : ''}
          access={fieldAccess('device.serial')} isMaster={isMaster} onChange={(v) => patchConn({ connSerial: v })} />
      ) : null}
      <Field type="string" label="Device ID" value={form.deviceId} access={fieldAccess('device.deviceId')} isMaster={isMaster}
        onChange={(v) => patch({ deviceId: v })} />
      <Field type="string" label="Backend ID" value={form.backendId} access={fieldAccess('device.backendId')} isMaster={isMaster}
        onChange={(v) => patch({ backendId: v })} placeholder="NULL" />
      <Field type="string" label="IP Address" value={form.ip} access={fieldAccess('device.ip')} isMaster={isMaster}
        onChange={(v) => patch({ ip: v })} />
      <Field type="string" label="Last Event" value={fmtLastEvent(form.lastEvent)} access={fieldAccess('device.lastEvent')} isMaster={isMaster}
        onChange={(v) => patch({ lastEvent: v })}
        hint={form.lastEvent ? 'As reported by device — not tz-adjusted' : undefined} />
      <Field type="string" label="Cameras" value={counts.authorized ? `${counts.enabled} of ${counts.authorized} enabled` : ''}
        access={fieldAccess('device.cameras')} isMaster={isMaster} onChange={(v) => patch({ camerasSummary: v })} />
    </div>
    ) : null}
    {/* Camera roster — a glanceable, SCROLL-CAPPED grid of this device's cameras (David #86bbfuajj:
        "show some of the camera details here without having to full switch tabs" + "make sure the
        container for these scrolls"). Click a card → Camera tab focused on it; "View all" → Camera tab
        (first camera). Replaces the old lone "View cameras" button. */}
    {/* The camera roster always shows its header when discovery is available (onDiscover), so the
        "Discover cameras" action is reachable even on a device with NO cameras yet — the case where
        it matters most. The glance grid renders when there are cameras; otherwise a short empty hint. */}
    {(camRoster.length || onDiscover) ? (
      <div className="setup-cam-roster">
        <div className="setup-cam-roster__head">
          <span className="setup-cam-roster__title">Cameras</span>
          <div className="setup-cam-roster__actions">
            {onDiscover ? (
              <button type="button" className="setup-cam-roster__discover" onClick={onDiscover}>
                <span className="setup-cam-roster__discover-ico" aria-hidden="true">⌖</span> Discover cameras
              </button>
            ) : null}
            {camRoster.length ? (
              <button type="button" className="setup-cam-roster__all" onClick={onViewCameras}>View all ›</button>
            ) : null}
          </div>
        </div>
        {camRoster.length ? (
          <div className="setup-cam-roster__grid">
            {camRoster.map((c) => (
              <RosterCard key={c.num} cam={c} onView={onViewCamera} onRemove={onRemoveCamera} onReplace={onReplaceCamera} />
            ))}
          </div>
        ) : (
          <div className="setup-cam-roster__empty">No cameras yet — discover them on this device’s network.</div>
        )}
      </div>
    ) : null}
  </div>
  );
};

// Device Config is FEATURE-GROUPED (David 2026-07-23): each subsystem = its switch(es) on top, then
// the read-only facts that switch uses right beneath (a collapsible "Details" block). Grounded in
// dragon/main.c — the enable toggle literally starts a thread that connects using those facts
// (llm.enabled → llm_thread_new(host,port,model); mpengine.enabled → mpengine_event_thread_new(host,
// port)) — so the toggle and the facts are ONE subsystem and belong together. Order is fixed per
// family: Cloud backend LEADS on dragon (David — it's the master cloud-connection switch, the first
// thing you'd check), then the feature subsystems, then the Media server security foot-gun last. The
// group label comes from here, NOT the raw section name (dragon [global] surfaces as "Cloud backend"
// — its use_backend key; legacy [global] as "Recording" — localtime/use_b2).
const DEVICE_GROUPS = {
  dragon: [
    { section: 'global',      label: 'Cloud backend', footgun: true },
    { section: 'llm',         label: 'Scene descriptions (LLM)' },
    { section: 'mpengine',    label: 'mpengine event bridge' },
    { section: 'videostore',  label: 'VideoStore' },
    { section: 'server',      label: 'Media server',  footgun: true },
  ],
  legacy: [
    { section: 'global',      label: 'Recording' },
    { section: 'libaiengine', label: 'Object detector' },
  ],
};

// Connector (POS) feature groups — read-only sections from files.connector.
// Mirrors the Java dview POS tab categorization.
const CONNECTOR_GROUPS = [
  { section: 'global', label: 'Connector settings' },
  { section: 'rpc',    label: 'RPC' },
  { section: 'dragon', label: 'Engine connection' },
  { section: 'cloud',  label: 'Cloud connection' },
];

// Friendly labels for the unmanaged [llm] keys the live edge build ships but the checked-out parser
// doesn't know (verified: scene_prompt/scene/objects have NO match in dragon/*.c, 2026-07-23). Shown
// READ-ONLY with the raw value — we humanize the key but don't assert behavior we can't ground in
// source (they graduate to managed Config fields once the parser is confirmed to accept them).
const LLM_OTHER_LABELS = { scene: 'Scene', objects: 'Objects', scene_prompt: 'Scene prompt' };

// Build the ordered feature groups for a family: per group, the writable toggle(s) in its section,
// the read-only facts in its section, and (LLM only) the unmanaged keys pulled up so the whole
// feature reads together. Empty groups (no toggle, no fact, no pulled-up key) drop out.
// PRESENT-ONLY (David 2026-08-26): only fields the device's blob actually carried (`has[form]`) are
// surfaced — we mirror exactly what the device sent, never seed a control for a key it omitted. The
// pulled-up LLM "other" keys are already present-only (they come from otherDeviceKeys).
// A config field/key's disposition comes ONLY from the single source (setupAccess), keyed by its
// section.key; unknown → default master:edit / local:readonly. TYPE (kind) is a SEPARATE axis and never
// gates. This is the one gate for every managed config input. (David 2026-08-27)
const cfgDisp = (section, key, isMaster) => resolveDisp({ access: fieldAccess(`${section}.${key}`) }, isMaster);

// Group the present device fields per subsystem. NO access-based split — every field renders through the
// same Field, and its own disp (from the single source) decides hidden/readonly/edit. conf_rev is
// rendered paired with Config Format at the top of Global (David #86bbfuajj), so it's kept off this list.
const buildDeviceGroups = (family, dFields, llmOther, isMaster, has) =>
  (DEVICE_GROUPS[family.key] || [])
    .map((g) => ({
      ...g,
      fields: dFields.filter((f) => f.section === g.section && (!has || has[f.form]) && f.key !== 'conf_rev'
        && cfgDisp(f.section, f.key, isMaster) !== 'hidden'),
      other: g.section === 'llm' ? llmOther : [],
    }))
    .filter((g) => g.fields.length || g.other.length);

// One subsystem group: header, then EVERY present field rendered the same way (the ONE Field), each carrying
// its own disp from the single source. Unmanaged ("other", e.g. the LLM scene keys) render through the
// same uniform UnknownField — editable by default, type inferred, gate-able per key. (David 2026-08-27)
const ConfigGroup = ({ group, cfg, patchCfg, patchOther, isMaster }) => (
  <div className="setup-cfg-group">
    {/* Footgun groups (Cloud backend, Media server) are Dividia-facing: their fields are all master-only,
        so for a non-master the header would sit orphaned over an unrelated field (e.g. Timezone in the
        global group). Suppress the header for a non-master; master's view is unchanged. (David 2026-09-23) */}
    {group.footgun && !isMaster ? null : <div className="setup-section">{group.label}</div>}
    {group.fields.map((f) => (
      <Field key={f.form} type={f.kind} label={f.label} value={cfg[f.form]}
        options={f.options === 'tz' ? timezoneSelectOptions(cfg[f.form]) : undefined}
        disp={cfgDisp(f.section, f.key, isMaster)} soleLabel={f.label === group.label}
        onChange={(v) => patchCfg({ [f.form]: v })} />
    ))}
    {group.other.length ? (
      <div className="setup-facts">
        {group.other.map((o, i) => (
          <UnknownField key={'other-' + i} label={LLM_OTHER_LABELS[o.key] || o.key} entry={o}
            onChange={patchOther} disp={cfgDisp(o.section, o.key, isMaster)} />
        ))}
      </div>
    ) : null}
  </div>
);

const CONFIG_SECTIONS = [
  { id: 'global', label: 'Global' },
  { id: 'videostore', label: 'VideoStore' },
  { id: 'server', label: 'Server' },
  { id: 'llm', label: 'LLM' },
  { id: 'mpengine', label: 'mpengine' },
];

const ConfigTab = ({ cfg, patchCfg, patchOther, groups, config, isMaster }) => {
  const [activeSection, setActiveSectionRaw] = useState('global');
  const setActiveSection = useCallback((id) => { setActiveSectionRaw(id); saveSetupSubTab('config', id); }, []);

  useEffect(() => {
    const restored = setupPersistedSubTab('config');
    if (restored && CONFIG_SECTIONS.some((s) => s.id === restored)) setActiveSectionRaw(restored);
  }, []);

  const knownIds = new Set(CONFIG_SECTIONS.map((s) => s.id));
  const sectionOf = (g) => knownIds.has(g.section) ? g.section : 'global';
  const visibleSections = CONFIG_SECTIONS.filter((s) => groups.some((g) => sectionOf(g) === s.id));
  const activeGroups = groups.filter((g) => sectionOf(g) === activeSection);

  return (
    <div className="setup-cfg-grid">
      <div className="setup-tabbar">
        {visibleSections.map((s) => (
          <button key={s.id} type="button"
            className={'setup-tabbar__tab' + (activeSection === s.id ? ' setup-tabbar__tab--active' : '')}
            onClick={() => setActiveSection(s.id)}>{s.label}</button>
        ))}
      </div>
      <div className="setup-tabbar__body">
        {activeSection === 'global' && isMaster && config && config.format != null ? (
          /* Config Format + revision are master-only (local:'hidden'); gate the whole wrapper on master so a
             non-master doesn't get an empty spacer div above Timezone. (review F1) */
          <div className="setup-facts setup-facts--mb-lg">
            <Field type="string" label="Config Format"
              value={cfg && cfg.confFormat != null ? cfg.confFormat : String(config.format)}
              access={fieldAccess('device.configFormat')} isMaster={isMaster} onChange={(v) => patchCfg({ confFormat: v })} />
            <Field type="string" label="Config revision"
              value={cfg && cfg.confRev != null ? String(cfg.confRev) : '0'}
              access={fieldAccess('device.confRev')} isMaster={isMaster} onChange={(v) => patchCfg({ confRev: v })} />
          </div>
        ) : null}
        {activeGroups.map((g) => (
          <ConfigGroup key={g.section} group={g} cfg={cfg} patchCfg={patchCfg} patchOther={patchOther} isMaster={isMaster} />
        ))}
      </div>
    </div>
  );
};

// MISC — the keys this viewer's schema does not model. SAME uniform mechanism as every other input:
// editable by default (master edits / local read-only), TYPE inferred from the value, and each key is
// gate-able (readonly/hidden) via the single source by its section.key. (David 2026-08-27)
const MiscTab = ({ entries, patchOther, isMaster }) => (
  <div><OtherSettings entries={entries} onChange={patchOther}
    dispOf={(e) => cfgDisp(e.section, e.key, isMaster)} /></div>
);

// Build the ordered connector (POS) groups for the POS ▸ Advanced overlay. A group shows ONLY the
// fields the connector blob actually carried (`has[form]`), plus the unmanaged passthrough keys.
// PRESENT-ONLY (David 2026-08-26): we mirror exactly what the device sent — a field the blob omitted is
// neither shown nor written, so we no longer seed controls for absent keys ("boil the lake" is retired).
// Empty groups drop out. A shown field still resolves per-viewer (master edits, local read-only) and a
// 'hidden'-access field is dropped regardless of presence.
const buildConnectorGroups = (conn, connOther, isMaster) =>
  CONNECTOR_GROUPS
    .map((g) => {
      const cFields = connectorFields().filter((f) => f.section === g.section);
      return {
        ...g,
        // Only fields the blob carried (conn.has[form]) AND not access-hidden for this viewer.
        fields: cFields.filter((f) => (conn.has ? conn.has[f.form] : true) && cfgDisp(f.section, f.key, isMaster) !== 'hidden'),
        other: connOther.filter((o) => o.section === g.section),
      };
    })
    .filter((g) => g.fields.length || g.other.length);

const noop = () => {};

// The fields of ONE connector group — SAME ONE Field as everywhere else. disp comes ONLY from the single
// source (cfgDisp by section.key); TYPE (f.kind) picks the widget. A bool keeps its phantom-label row for
// grid alignment. Unmanaged keys render through the same Field (via UnknownField's type-infer). Edits route
// through patchConn (managed) / patchConnOther (unknown). (David 2026-08-27)
const ConnFields = ({ group, conn, patchConn, patchConnOther, isMaster }) => (
  <div className="setup-facts">
    {group.fields.map((f) => {
      const disp = cfgDisp(f.section, f.key, isMaster);
      if (disp === 'hidden') return null;               // hidden HIDES the row — never a read-only fallback
      const node = (
        <Field key={f.form} type={f.kind} label={f.label} value={conn[f.form]} disp={disp}
          options={f.options} segmented={f.segmented} scale={f.scale}
          suffix={f.suffix ? (SUFFIX_LABELS[f.suffix] || f.suffix) : undefined}
          min={f.min} max={f.max} hint={f.hint}
          onChange={(v) => (patchConn || noop)({ [f.form]: v })} />
      );
      return f.kind === 'bool'
        ? <Row key={f.form} label={' '} className="setup-form__row--phantom-label">{node}</Row>
        : node;
    })}
    {group.other.map((o, i) => (
      <UnknownField key={'co-' + i} label={o.key} entry={o}
        onChange={patchConnOther || noop} disp={cfgDisp(o.section, o.key, isMaster)} />
    ))}
  </div>
);

// MOBILE-LANDSCAPE: a LEFT-hand vertical section menu + the selected section's data on the RIGHT —
// mirrors the device Config tab's left-rail concept (David 2026-08-24). #86bbfuajj
const ConnectorRail = ({ groups, conn, patchConn, patchConnOther, isMaster }) => {
  const [sel, setSel] = useState(groups[0] ? groups[0].section : null);
  const active = groups.find((g) => g.section === sel) || groups[0];
  return (
    <div className="setup-adv-2col">
      <nav className="setup-adv-rail" aria-label="Advanced sections">
        {groups.map((g) => (
          <button key={g.section} type="button"
            className={'setup-adv-rail__item' + (active && active.section === g.section ? ' setup-adv-rail__item--active' : '')}
            onClick={() => setSel(g.section)}>{g.label}</button>
        ))}
      </nav>
      <div className="setup-adv-content">
        {active ? <ConnFields group={active} conn={conn} patchConn={patchConn} patchConnOther={patchConnOther} isMaster={isMaster} /> : null}
      </div>
    </div>
  );
};

const ConnectorGroup = ({ group, conn, patchConn, patchConnOther, isMaster }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="setup-cfg-group">
      <button type="button" className="setup-section setup-section--collapsible" onClick={() => setOpen((o) => !o)}>
        <span className="setup-cfg-uses__chevron">{open ? '▼' : '▶'}</span> {group.label}
      </button>
      {open ? <ConnFields group={group} conn={conn} patchConn={patchConn} patchConnOther={patchConnOther} isMaster={isMaster} /> : null}
    </div>
  );
};

// A single POS field — renders the appropriate control for the field kind.
// Bool fields reuse the setup-switch toggle (same as Config tab toggles).
const SUFFIX_LABELS = { percent: '%', seconds: 'sec', pounds: 'lbs' };

// Within one grid group (a section body or a sub-section), half-width fields tile two-per-row: every
// int, plus any field explicitly marked `half` (e.g. Start/Finish transaction selects, Scale Network
// Address paired with Port). A CONTIGUOUS run of these with ODD length would leave its last one alone
// in a half-width cell with dead space beside it — so that trailing field spans full width instead.
// Returns the set of such field keys. Runs are broken by a full-width field (a text/select/bool that
// is NOT marked half, or an editor). (David 2026-08-20)
const fullWidthIntKeys = (fieldList) => {
  const keys = new Set();
  let runStart = -1;
  const flush = (end) => {
    if (runStart >= 0 && (end - runStart) % 2 === 1) keys.add(fieldList[end - 1].key);
    runStart = -1;
  };
  fieldList.forEach((f, i) => {
    if (f.kind === 'int' || f.half) { if (runStart < 0) runStart = i; }
    else flush(i);
  });
  flush(fieldList.length);
  return keys;
};

// A single-camera dropdown for the row editors: this device's enabled cameras as "N — name". Preserves
// an already-stored camera number that isn't in the enabled list (a disabled/removed cam) rather than
// silently dropping it, so an existing config round-trips.
const CameraSelect = ({ cameras, value, disabled, taken, onChange }) => {
  const val = value != null ? String(value) : '';
  const known = cameras.some((c) => String(c.num) === val);
  // A camera already chosen in ANOTHER row is greyed out (disabled) so the same camera can't be
  // associated twice — the LPR config de-dupes cam numbers, so a duplicate would just collapse.
  const inUse = (n) => !!taken && taken.has(String(n)) && String(n) !== val;
  const options = [
    { value: '', label: '— select camera —' },
    ...cameras.map((c) => ({ value: String(c.num), label: `${c.num} — ${c.name || 'Camera ' + c.num}${inUse(c.num) ? ' (in use)' : ''}`, disabled: inUse(c.num) })),
    ...(val && !known ? [{ value: val, label: `${val} — (not enabled)` }] : []),
  ];
  return (
    <SheetSelect className="setup-input setup-pos-cam-row__cam" value={val} disabled={disabled}
      title="Camera" options={options} onChange={onChange} />
  );
};

// Per-row overlay switch — the same setup-switch component as every other Setup toggle, compact.
// Per-row overlay switch. The visible "Overlay" text is dropped — the fixed column header labels it —
// so the switch fits the row on one line. aria-label keeps it accessible.
const OverlayToggle = ({ on, disabled, onChange }) => (
  <label className={'setup-switch-row setup-pos-cam-row__ov' + (disabled ? ' setup-switch-row--disabled' : '')}>
    <span className={'setup-switch' + (on ? ' setup-switch--on' : '')}>
      <input type="checkbox" className="setup-switch__input" checked={on} disabled={disabled} aria-label="Overlay"
        onChange={(e) => onChange(e.target.checked)} />
      <span className="setup-switch__track"><span className="setup-switch__thumb" /></span>
    </span>
  </label>
);

// Remove button for a camera row — a minus glyph in the danger colour (matches the "Remove" column header).
const CamRowDelete = ({ onClick, disabled }) => (
  <button type="button" className="setup-pos-cam-row__del" aria-label="Remove camera" onClick={onClick} disabled={disabled}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  </button>
);

// Shared shell for the camera row editors. The label + "+ Add camera" sit on ONE header row pinned at
// the TOP (so Add never scrolls out of reach as the list grows); the selected-camera rows go in a
// self-scrolling list beneath (the only scroll container on mobile base).
const CamerasField = ({ hint, disabled, addDisabled, onAdd, columns, hasRows, children }) => (
  <div className="setup-pos-field setup-pos-cams">
    <div className="setup-pos-cams__head">
      {!disabled ? <button type="button" className="setup-btn--add setup-pos-cam-add" onClick={onAdd} disabled={!!addDisabled}>+ Add camera</button> : null}
    </div>
    {/* The fixed column header IS the table heading (the redundant "Cameras" title is gone) — a sibling
        ABOVE the scroll list (never scrolls), shown once a camera is added. Same grid template as the rows
        so labels line up over their controls; it labels every column incl. Remove (right-edge balance). */}
    {hasRows && columns ? (
      <div className={'setup-pos-cam-list-head' + (columns.length > 3 ? ' setup-pos-cam-list-head--events' : '')} aria-hidden="true">
        {columns.map((c, i) => <span key={i} className="setup-pos-cam-head__col">{c}</span>)}
      </div>
    ) : null}
    <div className="setup-pos-cam-list">{children}</div>
    {hint ? <div className="setup-hint">{hint}</div> : null}
  </div>
);

// SW / PSCL camera-overlay editor — repeatable [camera][event][overlay] rows serialized to `images`
// (`~`-joined `cam-Event-overlay`). Same shell as the LPR editor; the event column is the only diff.
const OverlaysEditor = ({ field, value, cameras, events, disabled, onChange }) => {
  const rows = parseOverlays(value);
  const emit = (next) => onChange(field.key, serializeOverlays(next));
  const setRow = (i, patch) => emit(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const delRow = (i) => emit(rows.filter((_, j) => j !== i));
  const addRow = (cam) => emit([...rows, { cam, event: events[0] || '', overlay: true }]);
  const evOptions = events.map((ev) => ({ value: ev, label: ev }));
  return (
    <CamerasField label={field.label} hint={field.hint} disabled={disabled}
      onAdd={() => addRow(cameras[0] ? String(cameras[0].num) : '')}
      columns={['Camera', 'On event', 'Overlay', 'Remove']} hasRows>
      {rows.length ? rows.map((r, i) => (
        <div key={i} className="setup-pos-cam-row setup-pos-cam-row--events">
          <CameraSelect cameras={cameras} value={r.cam} disabled={disabled} onChange={(v) => (v ? setRow(i, { cam: v }) : delRow(i))} />
          <SheetSelect className="setup-input setup-pos-cam-row__ev" value={r.event} disabled={disabled}
            title="On event" options={evOptions}
            onChange={(v) => setRow(i, { event: v })} />
          <OverlayToggle on={r.overlay} disabled={disabled} onChange={(v) => setRow(i, { overlay: v })} />
          {!disabled ? <CamRowDelete onClick={() => delRow(i)} /> : null}
        </div>
      )) : (
        // Empty → a ready placeholder row so no "+ Add" click is needed. It carries NO data (nothing is
        // serialized without a camera ⇒ NOT dirty vs pristine); picking a camera promotes it to a real row
        // (event defaults to the first event). The event/overlay/remove stay inert until then.
        <div className="setup-pos-cam-row setup-pos-cam-row--events" key="__placeholder">
          <CameraSelect cameras={cameras} value="" disabled={disabled} onChange={(v) => v && addRow(v)} />
          <SheetSelect className="setup-input setup-pos-cam-row__ev" value="" disabled
            title="On event" options={[{ value: '', label: '— select event —' }, ...evOptions]} onChange={() => {}} />
          <OverlayToggle on={false} disabled />
          <CamRowDelete disabled />
        </div>
      )}
    </CamerasField>
  );
};

// LPR camera editor — repeatable [camera][overlay] rows. Selecting a camera associates it; the overlay
// switch adds it to the overlay list too. Writes BOTH `assoc` (every row) and `overlay` (checked rows)
// via two patchPos calls (see serializeLprCameras).
const LprCamerasEditor = ({ field, posSec, cameras, disabled, onChange }) => {
  const rows = parseLprCameras(posSec);
  const emit = (next) => { const { assoc, overlay } = serializeLprCameras(next); onChange('assoc', assoc); onChange('overlay', overlay); };
  const setRow = (i, patch) => emit(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const delRow = (i) => emit(rows.filter((_, j) => j !== i));
  // Cameras already associated (any row) — greyed out in every row's dropdown AND skipped when adding,
  // so "+ Add camera" grows the list with the next FREE camera instead of duplicating cam #1 (a
  // duplicate collapses, because the config de-dupes cam numbers). Add is disabled once all are used.
  const taken = new Set(rows.map((r) => r.cam).filter(Boolean));
  const nextFree = cameras.find((c) => !taken.has(String(c.num)));
  const addRow = (cam) => emit([...rows, { cam, overlay: false }]);
  // Split layout (David #86bbfuajj): "+ Add camera" is a COMPACT button that stays in its facts-grid cell
  // (beside Report Format) — a phantom label aligns it to the neighbour's input. The associated-camera
  // rows live in a SEPARATE full-width block below (they need the width), so the button never stretches
  // to full width or jumps to a new row when a camera is added.
  return (
    <>
      {!disabled ? (
        <div className="setup-pos-field setup-pos-cam-add-cell">
          <Row label={' '} className="setup-form__row--phantom-label">
            <button type="button" className="setup-btn--add setup-pos-cam-add" onClick={() => nextFree && addRow(String(nextFree.num))} disabled={!nextFree}>+ Add camera</button>
          </Row>
        </div>
      ) : null}
      <div className="setup-pos-field setup-pos-cams setup-pos-cams--rows-only">
        <div className="setup-pos-cam-list-head" aria-hidden="true">
          <span className="setup-pos-cam-head__col">Camera</span>
          <span className="setup-pos-cam-head__col">Overlay</span>
          <span className="setup-pos-cam-head__col">Remove</span>
        </div>
        <div className="setup-pos-cam-list">
          {rows.length ? rows.map((r, i) => (
            <div key={i} className="setup-pos-cam-row">
              <CameraSelect cameras={cameras} value={r.cam} disabled={disabled} taken={taken} onChange={(v) => (v ? setRow(i, { cam: v }) : delRow(i))} />
              <OverlayToggle on={r.overlay} disabled={disabled} onChange={(v) => setRow(i, { overlay: v })} />
              {!disabled ? <CamRowDelete onClick={() => delRow(i)} /> : null}
            </div>
          )) : (
            // Empty → a ready placeholder row so no "+ Add" click is needed. It writes NO assoc/overlay
            // without a camera (⇒ NOT dirty vs pristine); picking a camera promotes it to a real row. The
            // overlay + remove stay inert until then. Only shows while the list is empty.
            <div className="setup-pos-cam-row" key="__placeholder">
              <CameraSelect cameras={cameras} value="" disabled={disabled} taken={taken} onChange={(v) => v && addRow(v)} />
              <OverlayToggle on={false} disabled />
              <CamRowDelete disabled />
            </div>
          )}
        </div>
        {field.hint ? <div className="setup-hint">{field.hint}</div> : null}
      </div>
    </>
  );
};

// Facility Tracker (type 20) camera editor — the LPR [camera][overlay] list, but SPLIT into one flat group
// per event (FacilityIn / FacilityOut). The group label IS the event, so a camera can't be mis-tagged;
// each group owns its "+ Add camera" and rows. All groups serialize TOGETHER to the single `images` string
// as `~`-joined `cam-Event-overlay` triples (the SW/PSCL wire format). A camera used in EITHER group is
// "taken" (greyed elsewhere, skipped by Add) — one lane reads one direction. Mobile-first by construction:
// reuses CamerasField + setup-pos-cam-row + the flat-section label, inheriting every breakpoint the other
// POS camera editors already define. `events` comes from the schema field (inline `events`, or eventsFrom).
const displayEvent = (ev) => String(ev).replace(/([a-z])([A-Z])/g, '$1 $2');   // FacilityIn → "Facility In"
const EventCamerasEditor = ({ field, value, cameras, events, disabled, onChange }) => {
  const rows = parseOverlays(value);
  const emit = (next) => onChange(field.key, serializeOverlays(next));
  const setRow = (i, patch) => emit(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const delRow = (i) => emit(rows.filter((_, j) => j !== i));
  // A camera in ANY group is taken — greyed in every dropdown and skipped when adding, so a lane is never
  // assigned both directions and "+ Add camera" grows with the next FREE camera.
  const taken = new Set(rows.map((r) => r.cam).filter(Boolean));
  const nextFree = cameras.find((c) => !taken.has(String(c.num)));
  const addCam = (event, cam) => emit([...rows, { cam, event, overlay: false }]);
  return (
    <div className="setup-pos-event-cams">
      {(events || []).map((ev) => {
        // keep each row's index into the full list so edits/deletes address the right serialized entry
        const group = rows.map((r, i) => ({ r, i })).filter((x) => x.r.event === ev);
        return (
          <div key={ev} className="setup-pos-section setup-pos-section--flat">
            <div className="setup-pos-section__label--static">{displayEvent(ev)}</div>
            <div className="setup-pos-section__body">
              <CamerasField disabled={disabled} onAdd={() => nextFree && addCam(ev, String(nextFree.num))} addDisabled={!nextFree}
                columns={['Camera', 'Overlay', 'Remove']} hasRows>
                {group.length ? group.map(({ r, i }) => (
                  <div key={i} className="setup-pos-cam-row">
                    <CameraSelect cameras={cameras} value={r.cam} taken={taken} disabled={disabled}
                      onChange={(v) => (v ? setRow(i, { cam: v }) : delRow(i))} />
                    <OverlayToggle on={r.overlay} disabled={disabled} onChange={(v) => setRow(i, { overlay: v })} />
                    {!disabled ? <CamRowDelete onClick={() => delRow(i)} /> : null}
                  </div>
                )) : (
                  // Empty group → one ready placeholder row so no "+ Add" click is needed on initial create.
                  // It carries NO data (images stays empty ⇒ NOT dirty vs pristine); picking a camera promotes
                  // it to a real row. The remove button shows disabled (greyed) for column balance until then.
                  <div className="setup-pos-cam-row" key="__placeholder">
                    <CameraSelect cameras={cameras} value="" taken={taken} disabled={disabled}
                      onChange={(v) => v && addCam(ev, v)} />
                    <OverlayToggle on={false} disabled />
                    <CamRowDelete disabled />
                  </div>
                )}
              </CamerasField>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// A collapsible section within PosDetail — matches Brad's bordered card pattern.
const PosCollapsibleSection = ({ sectionId, label, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="setup-pos-section">
      <button type="button" className="setup-pos-section__header" onClick={() => setOpen((o) => !o)}>
        <span className="setup-pos-section__chevron">{open ? '▼' : '▶'}</span>
        <span className="setup-pos-section__label">{label}</span>
      </button>
      {/* data-section lets the stylesheet lay a specific section differently at desktop (e.g. RabbitMQ → 2-col). */}
      {open ? <div className="setup-pos-section__body" data-section={sectionId}>{children}</div> : null}
    </div>
  );
};

// Scale-sensing Enable — a setup-switch whose on/off is the presence of the [scaleN] section.
// Stays enabled even when sensing is off (so you can turn it on); it gates the other scale fields.
const ScaleEnableToggle = ({ on, disabled, onChange }) => (
  <div className="setup-pos-field setup-pos-field--bool">
    <div className="setup-cfg-toggle">
      <label className={'setup-switch-row' + (disabled ? ' setup-switch-row--disabled' : '')}>
        <span className={'setup-switch' + (on ? ' setup-switch--on' : '')}>
          <input type="checkbox" className="setup-switch__input" checked={on} disabled={disabled}
            onChange={(e) => onChange(e.target.checked)} />
          <span className="setup-switch__track"><span className="setup-switch__thumb" /></span>
        </span>
        <span className="setup-switch__label">Enable scale sensing <span className={'setup-switch__state' + (on ? ' setup-switch__state--on' : ' setup-switch__state--off')}>({on ? 'on' : 'off'})</span></span>
      </label>
    </div>
  </div>
);

// Enable/Disable POS — a setup-switch whose on/off IS the presence of the whole files.connector block
// (86bc1nuf9). It heads the POS tab's base list; when off, the tab shows only this toggle. Read-only
// (disabled) for a non-master — authoring the connector shell is connector-wide, like Advanced. Mirrors
// ScaleEnableToggle so the two connector toggles read the same.
const ConnectorEnableToggle = ({ on, disabled, onChange }) => (
  <div className="setup-pos-field setup-pos-field--bool setup-pos__enable">
    <div className="setup-cfg-toggle">
      <label className={'setup-switch-row' + (disabled ? ' setup-switch-row--disabled' : '')}>
        <span className={'setup-switch' + (on ? ' setup-switch--on' : '')}>
          <input type="checkbox" className="setup-switch__input" checked={on} disabled={disabled}
            aria-label="Enable POS" onChange={(e) => onChange(e.target.checked)} />
          <span className="setup-switch__track"><span className="setup-switch__thumb" /></span>
        </span>
        <span className="setup-switch__label">Enable POS <span className={'setup-switch__state' + (on ? ' setup-switch__state--on' : ' setup-switch__state--off')}>({on ? 'on' : 'off'})</span></span>
      </label>
    </div>
  </div>
);

// A Scale Watcher lane is [posN] (pos-store fields) + an optional [scaleN] (scale-store fields).
// posSec is always present; scaleSec is undefined when scale sensing is disabled.
// MOBILE-LANDSCAPE POS detail: a LEFT vertical section menu + the selected section's content on the
// RIGHT, mirroring the Advanced rail and the device Config left-rail. Portrait keeps the accordion.
// Reuses the shared .setup-adv-* rail classes. (#86bbfuajj)
const PosSectionRail = ({ sections, renderBody }) => {
  const [sel, setSel] = useState(sections[0] ? sections[0].id : null);
  const active = sections.find((s) => s.id === sel) || sections[0];
  return (
    <div className="setup-adv-2col">
      <nav className="setup-adv-rail" aria-label="POS sections">
        {sections.map((s) => (
          <button key={s.id} type="button"
            className={'setup-adv-rail__item' + (active && active.id === s.id ? ' setup-adv-rail__item--active' : '')}
            onClick={() => setSel(s.id)}>{s.label}</button>
        ))}
      </nav>
      <div className="setup-adv-content">
        {active ? <div className="setup-facts" data-section={active.id}>{renderBody(active)}</div> : null}
      </div>
    </div>
  );
};

const PosDetail = ({ num, posSec, scaleSec, cameras, deviceName, isNew, onExitDevice, onBack, onChange, onDelete, onSetScaleEnabled, canManage, isMaster, otherPorts, pristinePort }) => {
  const landscape = useIsSetupMobileLandscape();   // POS sections: left-rail in mobile-landscape, accordion otherwise
  const typeNum = Number(posSec.type);
  const schema = POS_TYPES[typeNum];
  if (!schema) {
    return (
      <div>
        <button type="button" className="setup-back-link" onClick={onBack}>‹ POS Sources</button>
        <div className="setup-detail-title">Unknown POS type {typeNum}</div>
      </div>
    );
  }

  const sections = schema.sections || [];
  const fields = schema.fields || [];
  // Per-item access (rights-acl §2): each field/section resolves to 'edit' | 'readonly' | 'hidden' for
  // THIS viewer via its access:{master,local} (default: master edit / local readonly). Hidden items are
  // dropped from view; their config value is left untouched in form.config, so hiding never drops a key.
  // SECTION-level gate: a POS schema SECTION can carry its own access (resolveDisp on the section item).
  const dispOf = (item) => resolveDisp(item, isMaster);
  const itemHidden = (item) => dispOf(item) === 'hidden';
  const itemReadOnly = (item) => dispOf(item) !== 'edit';          // per-viewer: editable only where the slot is 'edit'
  // FIELD-level gate: every POS config option resolves through the ONE gate via the access map
  // ('pos.<key>'), exactly like the Device Config tab — master/local × edit/readonly/hidden. (#86bbm6j8w)
  const fieldDisp = (f) => cfgDisp('pos', f.key, isMaster);
  const fieldHidden = (f) => fieldDisp(f) === 'hidden';
  const fieldReadOnly = (f) => fieldDisp(f) !== 'edit';
  const canEditPos = true;                                         // POS create/delete/scale-enable: any admin on the Device screen, incl. local admins (#86bbm6j8w)
  const topSections = sections.filter((s) => !s.parent && !itemHidden(s));
  const scaleOn = !!scaleSec;

  const storeOf = (sectionId) => { const s = sections.find((x) => x.id === sectionId); return (s && s.store) || 'pos'; };
  // Read a field's live value from the section its store points at ([posN] or [scaleN]),
  // falling back to the schema default when the key/section is absent.
  //
  // `storedDefault` overrides that fallback for READS only (#86bc2p0fh). `default` is the value a
  // NEW lane is seeded with — and a seeded lane always writes the key, so this fallback only ever
  // fires for an EXISTING lane whose stored section omits it. There the honest answer is what the
  // connector itself does with a missing key, which is not always what we want to seed. ticket_source
  // is exactly that case: new lanes seed commandcloud, but connector_config.py reads an absent
  // ticket_source as 'apex', so a fielded lane without the key must keep reading apex or the page
  // tells the operator the wrong ticket source for a working lane.
  const rawValue = (key) => {
    const fld = fields.find((x) => x.key === key);
    if (!fld) return undefined;
    const src = storeOf(fld.section) === 'scale' ? (scaleSec || {}) : posSec;
    if (src[key] != null) return src[key];
    return fld.storedDefault !== undefined ? fld.storedDefault : fld.default;
  };
  const fieldVisible = (f) => (!f.showWhen || (f.showWhen.in || []).map(String).includes(String(rawValue(f.showWhen.field))))
    && !fieldHidden(f);
  // A field is disabled when it's read-only for this viewer OR it's a scale field with sensing off.
  const isDisabled = (f) => fieldReadOnly(f) || (storeOf(f.section) === 'scale' && !scaleOn);
  const handleChange = (key, value) => onChange(num, key, value);

  const renderFields = (list) => list.map((f) => {
    if (f.kind === 'overlays') {
      return <OverlaysEditor key={f.key} field={f} value={rawValue(f.key)} cameras={cameras}
        events={eventOptions(schema, f.eventsFrom)} disabled={isDisabled(f)} onChange={handleChange} />;
    }
    if (f.kind === 'lprCameras') {
      return <LprCamerasEditor key={f.key} field={f} posSec={posSec} cameras={cameras}
        disabled={isDisabled(f)} onChange={handleChange} />;
    }
    if (f.kind === 'eventCameras') {
      return <EventCamerasEditor key={f.key} field={f} value={rawValue(f.key)} cameras={cameras}
        events={f.events || eventOptions(schema, f.eventsFrom)} disabled={isDisabled(f)} onChange={handleChange} />;
    }
    // The ONE Field wrapped in a POS LAYOUT cell — the .setup-pos-field--* classes are pure grid layout
    // (half-width pairs / full-span / segmented styling), NOT another input. Field renders the widget inside.
    const kindCls = f.kind === 'bool' ? 'bool' : f.kind === 'select' ? 'select' : (f.kind === 'int' || f.kind === 'num') ? 'int' : 'text';
    const wrap = 'setup-pos-field setup-pos-field--' + kindCls
      + (f.half ? ' setup-pos-field--half' : '')
      + ((f.full || (list.__full && list.__full.has(f.key))) ? ' setup-pos-field--fullspan' : '');
    // Inline (real-time) port feedback: the tcp_port field reddens + shows a message when out-of-range or
    // colliding with another POS on the device; other fields never compute one. (#86bbpgbdz)
    const portErr = f.key === 'tcp_port' && portRangeOf(schema) ? tcpPortError(f, rawValue(f.key), otherPorts, pristinePort) : '';
    return (
      <div key={f.key} className={wrap}>
        <Field type={f.kind} label={f.label} value={rawValue(f.key)}
          disp={fieldDisp(f)}
          disabled={storeOf(f.section) === 'scale' && !scaleOn}
          options={f.options} segmented={f.segmented} scale={f.scale}
          suffix={f.suffix ? (SUFFIX_LABELS[f.suffix] || f.suffix) : undefined}
          min={f.min} max={f.max} hint={f.hint}
          invalid={!!portErr} error={portErr}
          onChange={(v) => handleChange(f.key, v)} />
      </div>
    );
  });

  // The rendered body of ONE top section (enable toggle + its fields + child subsections). Shared by the
  // portrait accordion AND the mobile-landscape section rail. (#86bbfuajj)
  const sectionBody = (section) => {
    const visible = fields.filter((f) => f.section === section.id && fieldVisible(f));
    visible.__full = fullWidthIntKeys(visible);
    const childSections = sections.filter((s) => s.parent === section.id && !itemHidden(s));
    return (
      <>
        {section.enableToggle ? (
          <ScaleEnableToggle on={scaleOn} disabled={itemReadOnly(section)} onChange={(v) => onSetScaleEnabled(num, v)} />
        ) : null}
        {renderFields(visible)}
        {childSections.map((child) => {
          const cvisible = fields.filter((f) => f.section === child.id && fieldVisible(f));
          cvisible.__full = fullWidthIntKeys(cvisible);
          return (
            <div key={child.id} className="setup-pos-subsection">
              <div className="setup-pos-subsection__label">{child.label}</div>
              {renderFields(cvisible)}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="setup-pos-detail">
      {/* One combined breadcrumb + a device-name header + an Add/Modify action title — the drill's own bar
          is suppressed here (bare), reclaiming the vertical space the double breadcrumb + tab bar used. (3R) */}
      <div className="setup-pos-detail__crumbs">
        <button type="button" className="setup-back-link" onClick={onExitDevice}>‹ Devices</button>
        <button type="button" className="setup-back-link" onClick={onBack}>‹ POS Sources</button>
        <span className="setup-pos-detail__device">{deviceName}</span>
        <span className="setup-pos-detail__action">{isNew ? 'Add POS' : 'Modify POS'}</span>
      </div>
      <div className="setup-pos-detail__title-row">
        <div className="setup-detail-title">{posSec.name || 'POS ' + num}</div>
        <Badge color="#1976d2">{schema.shortName}</Badge>
        {canEditPos ? (
          <button type="button" className="setup-pos-detail__delete" onClick={() => onDelete(num)} aria-label="Delete POS source">
            <svg className="setup-pos-detail__delete-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            <span>Delete</span>
          </button>
        ) : null}
      </div>

      {landscape && !schema.flatSections && topSections.length > 1 ? (
        <PosSectionRail sections={topSections} renderBody={sectionBody} />
      ) : schema.flatSections ? (
        /* Flat schemas (LPR): no rail, no section cards. All fields (incl. the camera editor) group into
           ONE .setup-facts grid (2/3-col, labels-above) as a single scrolling page. #86bbfuajj */
        <div className="setup-pos-detail__sections">
          <div className="setup-facts">
            {topSections.map((section) => <React.Fragment key={section.id}>{sectionBody(section)}</React.Fragment>)}
          </div>
        </div>
      ) : (
        /* Non-flat (portrait / single section): the collapsible accordion. */
        <div className="setup-pos-detail__sections">
          {topSections.map((section, idx) => (
            <PosCollapsibleSection key={section.id} sectionId={section.id} label={section.label} defaultOpen={idx === 0}>
              {sectionBody(section)}
            </PosCollapsibleSection>
          ))}
        </div>
      )}
    </div>
  );
};

// A POS source rendered as a roster CARD — mirrors the camera RosterCard (a defined container: a main
// open-button with a type-badge + name top line and a detail sub-line) so the device's POS list and Cameras
// list read the same. The one difference: POS has no per-card ⋯ menu — the corner action IS the remove '-'
// (delete stays confirm-guarded upstream in deletePos). (#86bbpgbdz)
const PosRosterCard = ({ num, sec, schema, canManage, onOpen, onRemove }) => {
  const typeNum = Number(sec.type);
  const typeName = schema ? schema.shortName : 'Type ' + typeNum;
  const hasPort = schema && (schema.fields || []).some((f) => f.key === 'tcp_port');
  const name = sec.name || ('POS ' + num);
  return (
    <div className="setup-cam-roster__card setup-pos-roster__card">
      <button type="button" className="setup-cam-roster__card-main" onClick={() => onOpen(num)}>
        <span className="setup-cam-roster__card-top">
          <Badge color="#1976d2">{typeName}</Badge>
          <span className="setup-cam-roster__name">{name}</span>
        </span>
        {/* Sub-line: the listen port for port-bearing types; a blank line (nbsp) for port-less types — kept
            present so every card holds a uniform height across the grid (no "Facility Tracker"/"Port —" text). */}
        <span className="setup-cam-roster__card-sub">
          <span className="setup-cam-roster__card-ip">{hasPort ? 'Port ' + (sec.tcp_port || '—') : ' '}</span>
        </span>
      </button>
      {canManage && onRemove ? (
        <button type="button" className="setup-cam-roster__menu-btn setup-pos-roster__remove"
          aria-label={'Remove ' + name} onClick={() => onRemove(num)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </button>
      ) : null}
    </div>
  );
};

const PosTab = ({ conn, groups, posList, config, pristineConn, canManage, connectorEnabled, onSetConnectorEnabled, showAdvanced, onCloseAdvanced, onToggleAdvanced, onPosViewChange, onExitDevice, deviceName, deviceId, token, onPatchPos, onPatchConn, onPatchConnOther, onAddPos, onDeletePos, onSetScaleEnabled, onRestoreConfig, isMaster }) => {
  const advLandscape = useIsSetupMobileLandscape();   // Advanced overlay: left-rail in mobile-landscape, accordion otherwise
  const [selectedPos, setSelectedPos] = useState(null);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [addType, setAddType] = useState(POS_TYPE_LIST[0].type);  // desktop inline-add: selected POS type
  const [posIsNew, setPosIsNew] = useState(false);   // Add (true) vs Modify (false) — drives the detail action title (3R)
  const overlayRef = useRef(null);
  const posSnapshotRef = useRef(null);   // config snapshot when a POS detail opens — Discard rolls back to it (#86bbm58f7 piece 3)

  // Report whether we're on the base POS list (not a detail / add-type sheet) so the parent can show the
  // connector-wide Advanced button only there. (#86bbfuajj 3O)
  useEffect(() => { if (onPosViewChange) onPosViewChange(selectedPos == null && !showAddSheet); },
    [selectedPos, showAddSheet, onPosViewChange]);
  // On unmount (e.g. leaving the device via "‹ Devices" while a POS detail is open) reset the parent's
  // view flag, so the NEXT device's POS tab doesn't inherit a stale "in a detail" state (hidden tab bar
  // / bare drill). (review #4)
  useEffect(() => () => { if (onPosViewChange) onPosViewChange(true); }, [onPosViewChange]);
  // If the selected POS section vanishes from config, clear the selection in an EFFECT — not during
  // render, where setState is a React-18/StrictMode hazard. (review #7)
  useEffect(() => {
    if (selectedPos != null) {
      const conn = (config.files && config.files.connector) || {};
      if (!conn['pos' + selectedPos]) setSelectedPos(null);
    }
  }, [selectedPos, config]);

  useEffect(() => {
    if (!showAdvanced) return;
    const handler = (e) => {
      // Ignore mousedown on the Advanced TOGGLE buttons (header tab action or the landscape body
      // button) — they own open/close; otherwise this mousedown-close races the button's toggle and
      // reopens it. (#86bbfuajj)
      // Footer actions (Save / Revert / Push) live OUTSIDE the overlay but must NOT close it (#86bbm58f7
      // piece 2) — the overlay closes only via its X or a genuine outside-overlay click.
      // A modal Sheet layered ABOVE Advanced — the Push/Revert review sheet or a confirm dialog, both
      // rendered as `.app-sheet` — also sits outside the overlay DOM. Clicking inside it (e.g. "Update
      // now") must NOT trip this close: doing so popped a spurious KEEP/DISCARD mid-save, so the first
      // Save click only dismissed that and the SECOND click actually saved. You're interacting with the
      // top layer, not dismissing Advanced. (#86bc2p0fh)
      // `.app-sheet-scrim` is the sheet's SCRIM — a SIBLING of the panel (common/Sheet.js), so it is
      // not matched by `.app-sheet`. Clicking it is how you dismiss the sheet, and that mousedown was
      // still closing Advanced underneath with the same spurious KEEP/DISCARD, just on the other
      // dismiss path. (#86bc2p0fh)
      if (e.target.closest && e.target.closest('.setup-pos__advanced, .setup-tabbar__action, .setup-drill__actions, .app-sheet, .app-sheet-scrim')) return;
      if (overlayRef.current && !overlayRef.current.contains(e.target)) onCloseAdvanced();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAdvanced, onCloseAdvanced]);

  // POS-level actions (Add) AND the connector-level Advanced fields follow the same default access —
  // for now master edits, local read-only.
  const canEditPos = true;   // POS create/delete: any admin on the Device screen, incl. local admins (#86bbm6j8w)

  // POS staged-draft (#86bbm58f7 piece 3): a POS detail snapshots the config when it OPENS; edits go live
  // into the staged bundle, but LEAVING the detail rolls back (Discard) or commits (Keep). A NEW POS is
  // snapshotted BEFORE it's added, so Discard removes the lane entirely — it only "sticks" on Keep. (The
  // device-tab bar is hidden inside a POS detail, so the two back-links are the only ways out to gate.)
  const openNewPos = (type) => {
    const snap = JSON.parse(JSON.stringify(config));
    const n = onAddPos(type);
    if (n) { posSnapshotRef.current = snap; setPosIsNew(true); setSelectedPos(n); }
  };
  const openExistingPos = (num) => {
    posSnapshotRef.current = JSON.parse(JSON.stringify(config));
    setPosIsNew(false); setSelectedPos(num);
  };
  const leavePos = async (after) => {
    const snap = posSnapshotRef.current;
    if (snap && !canonConfigEqual(config, snap)) {
      const discard = await askConfirm({
        title: posIsNew ? 'Discard new POS source?' : 'Discard changes to this POS?',
        message: posIsNew
          ? 'This POS source has not been added to the device yet. Discard it, or keep it staged?'
          : 'You have unsaved changes to this POS source. Discard them, or keep them staged?',
        confirmLabel: 'Discard', cancelLabel: 'Keep', danger: true,
      });
      if (discard && onRestoreConfig) onRestoreConfig(snap);
    }
    posSnapshotRef.current = null;
    after();
  };
  // A port-bearing type whose range is fully allocated can't take another lane — disable its Add affordance
  // (mobile sheet option + desktop picker/button) rather than let the user stage an un-pushable duplicate. (#86bbpgbdz)
  const rangeFull = (typeNum) => { const sch = POS_TYPES[Number(typeNum)]; return !!portRangeOf(sch) && nextFreePort(sch, config) == null; };

  if (selectedPos != null) {
    const connSec = (config.files && config.files.connector) || {};
    const posSec = connSec['pos' + selectedPos];
    if (!posSec) return null;   // an effect clears the now-stale selection (review #7)
    return (
      <PosDetail num={selectedPos} posSec={posSec} scaleSec={connSec['scale' + selectedPos]}
        cameras={enabledCameras(config)}
        deviceName={deviceName} isNew={posIsNew} onExitDevice={onExitDevice}
        onBack={() => leavePos(() => setSelectedPos(null))}
        onChange={onPatchPos}
        onDelete={async (n) => { const ok = await onDeletePos(n); if (ok) setSelectedPos(null); }}
        onSetScaleEnabled={onSetScaleEnabled}
        otherPorts={portOwnersExcept(config, selectedPos)}
        pristinePort={((pristineConn || {})['pos' + selectedPos] || {}).tcp_port}
        canManage={canManage} isMaster={isMaster} />
    );
  }

  return (
    <div className="setup-pos">
      {/* Enable/Disable POS (86bc1nuf9) = the presence of the whole connector block. The toggle heads the
          base list; OFF ⇒ only the toggle + a hint (Add/Advanced/list stay hidden until POS is seeded). It's
          master-gated (connector-wide, like Advanced); a non-master never reaches a POS-less tab anyway. */}
      <ConnectorEnableToggle on={connectorEnabled} disabled={!isMaster} onChange={onSetConnectorEnabled} />
      {/* ScaleWatcher (#86bc6dkfa): the cloud side of ticketing, beside the device-side Enable POS. MASTER-ONLY
          (cloudapi re-checks); shown whether or not POS is on, since enrollment is independent of the block. */}
      {isMaster && deviceId ? <ScalewatcherRow deviceId={deviceId} deviceName={deviceName} token={token} /> : null}
      {!connectorEnabled ? (
        <div className="setup-empty setup-pos__disabled-hint">POS is off for this device. Turn it on to capture register and scale transactions, then add POS sources.</div>
      ) : (
      <>
      {/* Action row — Add + Advanced. In mobile-landscape (CSS) this becomes a flex row PINNED above
          the scrolling POS list, so both stay reachable when the list overflows; Advanced is the
          narrower, secondary action beside the wider Add. In portrait it is a plain block (Add renders
          full-width exactly as before) and the body Advanced button is HIDDEN — portrait keeps Advanced
          in the header tab strip (.setup-tabbar__action). (#86bbfuajj) */}
      <div className="setup-pos__actions">
        {canEditPos ? (
          <>
            {/* mobile/tablet: full-width button → bottom-sheet type picker (unchanged) */}
            <button type="button" className="setup-btn--add setup-pos__add-btn" onClick={() => { onCloseAdvanced(); setShowAddSheet(true); }}>+ Add POS Source</button>
            {/* desktop: inline [type ▾] [+ Add] — no sheet (David #86bbfuajj, matches Brad's connector.html).
                CSS reveals this at ≥1280 and hides the full-width button there. */}
            <div className="setup-pos__add-inline">
              <select className="setup-select setup-pos__add-type" value={addType}
                onChange={(e) => setAddType(Number(e.target.value))} aria-label="POS type to add">
                {POS_TYPE_LIST.map((t) => <option key={t.type} value={t.type} disabled={rangeFull(t.type)}>{t.name} — Type {t.type}{rangeFull(t.type) ? ' (ports full)' : ''}</option>)}
              </select>
              <button type="button" className="setup-btn--add setup-pos__add-go" disabled={rangeFull(addType)}
                onClick={() => { onCloseAdvanced(); openNewPos(addType); }}>+ Add POS Source</button>
            </div>
          </>
        ) : null}
          {/* Advanced (connector-wide config) is MASTER-ONLY — hidden for local admins, whose connector
              fields are all local:hidden so it would open to nothing (#86bbm6j8w). */}
          {isMaster ? (
            <button type="button"
              className={'setup-tabbar__action setup-pos__advanced' + (showAdvanced ? ' setup-tabbar__action--active' : '')}
              onClick={() => { setShowAddSheet(false); onToggleAdvanced && onToggleAdvanced(); }}>Advanced</button>
          ) : null}
      </div>

      {/* POS sources render as roster CARDS mirroring the device's camera roster (defined container, type
          badge + name, a detail sub-line) — 1-col on mobile, 2-col at tablet-landscape/desktop (#86bbpgbdz). */}
      <div className="setup-pos__list setup-pos-roster__grid">
        {posList.length > 0 ? (
          posList.map((num) => {
            const sec = posSection(config, num);
            const schema = POS_TYPES[Number(sec.type)];
            return (
              <PosRosterCard key={num} num={num} sec={sec} schema={schema}
                canManage={canEditPos} onOpen={openExistingPos} onRemove={onDeletePos} />
            );
          })
        ) : (
          <div className="setup-empty">No POS sources configured.</div>
        )}
      </div>

      {showAddSheet ? (
        <Sheet className="app-sheet" title="Add POS Source" onClose={() => setShowAddSheet(false)}>
          {POS_TYPE_LIST.map((t) => (
            <button key={t.type} type="button" className="setup-pos-sheet__option" disabled={rangeFull(t.type)}
              onClick={() => { openNewPos(t.type); setShowAddSheet(false); }}>
              <span className="setup-pos-sheet__option-name">{t.name}{rangeFull(t.type) ? ' (ports full)' : ''}</span>
              <span className="setup-pos-sheet__option-desc">Type {t.type} — {t.shortName}</span>
            </button>
          ))}
        </Sheet>
      ) : null}

      {showAdvanced && !showAddSheet ? (
        <div className="setup-advanced-overlay" ref={overlayRef}>
          <div className="setup-advanced-overlay__header">
            <span className="setup-advanced-overlay__title">Advanced</span>
            <button type="button" className="setup-advanced-overlay__close" onClick={onCloseAdvanced}>&times;</button>
          </div>
          <div className="setup-advanced-overlay__body">
            {advLandscape
              ? <ConnectorRail groups={groups} conn={conn} patchConn={onPatchConn} patchConnOther={onPatchConnOther} isMaster={isMaster} />
              : groups.map((g) => (
                <ConnectorGroup key={g.section} group={g} conn={conn} patchConn={onPatchConn} patchConnOther={onPatchConnOther} isMaster={isMaster} />
              ))}
          </div>
        </div>
      ) : null}
      </>
      )}
    </div>
  );
};

export { CloudDeviceSetup }; // bare, for unit tests (pass sSess/bRole/fAdmin as props)

const mapStateToProps = (state) => ({
  sSess: state.auth.sSess,
  bRole: state.user.bRole,      // 0/1 master bit (1 = Dividia master)
  fAdmin: state.user.fAdmin,    // ADMIN right — a site-admin (bRole 0) edits via this
});

export default connect(mapStateToProps)(CloudDeviceSetup);
